import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import 'ol/ol.css';
import { Map, View } from 'ol';
import { defaults as defaultControls } from 'ol/control';
import Overlay from 'ol/Overlay';
import TileLayer from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import { fromLonLat, toLonLat } from 'ol/proj';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { LineString, Point } from 'ol/geom';
import { Feature } from 'ol';
import { Circle as CircleStyle, Fill, Icon, RegularShape, Stroke, Style, Text } from 'ol/style';
import axios from 'axios';
import { CHANGELOG, APP_VERSION } from '../changelog';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL
    || (typeof window !== 'undefined' && window.location.port === '3000' ? 'http://localhost:9082' : '');
const AUTH_STORAGE_KEY = 'routePlannerAuth';
const NAVIGATION_VOICE_STORAGE_KEY = 'routePlannerNavigationVoice';
const LAST_TRIP_STORAGE_KEY = 'routePlannerLastTrip';
const NAV_MODE_STORAGE_KEY = 'routePlannerNavigationMode';
const VEHICLE_STORAGE_KEY = 'routePlannerVehicle';
const SEEN_VERSION_STORAGE_KEY = 'routePlannerSeenVersion';
const IOS_INSTALL_HINT_STORAGE_KEY = 'routePlannerIosInstallHint';
const DEFAULT_TRAVEL_MODE_STORAGE_KEY = 'routePlannerDefaultTravelMode';

const loadStoredTravelMode = () => {
    try {
        const stored = window.localStorage.getItem(DEFAULT_TRAVEL_MODE_STORAGE_KEY);
        return stored === 'car' || stored === 'bike' || stored === 'foot' ? stored : 'car';
    } catch (storageError) {
        return 'car';
    }
};

const DEFAULT_VEHICLE = { fuelType: 'ev', consumption: 18, capacity: 60, startLevel: 80 };

const loadStoredVehicle = () => {
    try {
        const stored = window.localStorage.getItem(VEHICLE_STORAGE_KEY);
        return stored ? { ...DEFAULT_VEHICLE, ...JSON.parse(stored) } : DEFAULT_VEHICLE;
    } catch (storageError) {
        return DEFAULT_VEHICLE;
    }
};

const VEHICLE_UNITS = { ev: 'kWh', petrol: 'l', gas: 'kg' };
const MAP_MIN_ZOOM = 5;
// Zoom 19 = OSM-karttatasolla talonumerot (esim. "Eerikinkatu 6") näkyvät selvästi.
const MAP_MAX_ZOOM = 19;
const RAIN_RADAR_MAX_ZOOM = 8;
const NAV_VIEW_RESTORE_DELAY_MS = 3_000;
const GPS_MAX_REASONABLE_ACCURACY_METERS = 85;
const GPS_STALE_SAMPLE_MS = 15_000;
const OFF_ROUTE_BASE_THRESHOLD_METERS = 120;
const OFF_ROUTE_CONFIRMATION_COUNT = 3;
const OFF_ROUTE_CONFIRMATION_MS = 7_000;
// Selvä poikkeama (esim. risteyksestä ajettiin ohi): reititetään nopeammin uudelleen.
const OFF_ROUTE_HARD_MULTIPLIER = 2.2;
const OFF_ROUTE_HARD_CONFIRMATION_COUNT = 2;
const OFF_ROUTE_HARD_CONFIRMATION_MS = 3_000;
// Dead reckoning: kun GPS-fix vanhenee (tunneli, katve, kaupungin kuilut), jatketaan
// matkaa reittiä pitkin viimeksi mitatulla nopeudella, jotta auto ei "pysähdy seinään"
// eikä kartta ala hyppiä. Aletaan arvioida 2,5 s kuluttua, mutta korkeintaan 45 s.
const DEAD_RECKON_START_MS = 2_500;
// Kuinka kauan jatketaan reittiä pitkin ilman GPS-fixiä ennen kuin jäädään odottamaan.
// Pitkä ikkuna (5 min): ei keskeytetä navigointia lyhyestä katkosta – jos ajetaan eikä
// olla vielä perillä, yritetään navigoida loppua kohti (tunnelit, pitkät katveet).
const DEAD_RECKON_MAX_MS = 300_000;
const DEAD_RECKON_TICK_MS = 1_000;
// Etene ennustuksessa vain jos viimeksi mitattu nopeus ylittää tämän: jos auto
// seisoo (GPS ~0), ei arvata etenemistä – navigointi ei liiku itsenäisesti paikallaan.
const DEAD_RECKON_MIN_MOVING_KMH = 6;
const TRANSPARENT_TILE = 'data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=';
const SPEED_CAMERA_SOURCE_INFO = {
    dataSource: 'OpenStreetMap via Overpass API',
    sourceUrl: 'https://www.openstreetmap.org/copyright',
    license: 'Open Database License (ODbL)',
};
const ROAD_CONTEXT_SOURCE_INFO = {
    dataSource: 'OpenStreetMap via Overpass API',
    sourceUrl: 'https://www.openstreetmap.org/copyright',
    license: 'Open Database License (ODbL)',
};
const FINNISH_NAVIGATION_VOICE_HINTS = [
    'satu',
    'heidi',
    'microsoft heidi',
    'finnish',
    'suomi',
    'suomen',
];
const NATURAL_VOICE_HINTS = [
    'natural',
    'premium',
    'enhanced',
    'neural',
    'online',
    'apple',
    'microsoft',
    'google',
];
const SECONDARY_FEMALE_VOICE_HINTS = [
    'flo',
    'samantha',
    'victoria',
    'ava',
    'allison',
    'susan',
    'serena',
    'karen',
    'moira',
    'tessa',
    'zira',
    'jenny',
    'aria',
];
const NOVELTY_VOICE_HINTS = [
    'alex',
    'fred',
    'ralph',
    'zarvox',
    'trinoids',
    'whisper',
    'bells',
    'boing',
    'bad news',
    'bahh',
    'cellos',
    'deranged',
    'good news',
    'hysterical',
    'junior',
    'kathy',
    'organ',
    'princess',
];

const routeColors = ['#1d4ed8', '#0f766e', '#b45309'];
const windIconSvg = encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 34 34">
  <path d="M17 3 C13 9 10 13 10 18 a7 7 0 0 0 14 0 C24 13 21 9 17 3Z" fill="#0f766e" stroke="#ffffff" stroke-width="3" stroke-linejoin="round"/>
  <path d="M17 8 v14" stroke="#ffffff" stroke-width="2.4" stroke-linecap="round"/>
  <path d="M13.5 18 17 22l3.5-4" fill="none" stroke="#ffffff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`);
const windIconSrc = `data:image/svg+xml;charset=UTF-8,${windIconSvg}`;

const riskLabels = {
    NONE: 'Normaali',
    LOW: 'Pieni huomio',
    MEDIUM: 'Kohtalainen',
    HIGH: 'Huono sää',
    UNKNOWN: 'Ei dataa',
};

const riskColors = {
    NONE: '#15803d',
    LOW: '#65a30d',
    MEDIUM: '#ca8a04',
    HIGH: '#dc2626',
    UNKNOWN: '#64748b',
};

const travelModes = [
    { value: 'car', label: 'Auto' },
    { value: 'bike', label: 'Polkupyörä' },
    { value: 'foot', label: 'Kävely' },
];

const routePreferences = [
    { value: 'balanced', label: 'Paras sää' },
    { value: 'tailwind', label: 'Myötätuulisin' },
];
const WEATHER_POLL_INTERVAL_MS = 5 * 60 * 1000;

const weatherSnapshot = (route) => {
    const points = route?.weatherPoints;
    if (!Array.isArray(points) || !points.some((point) => Number.isFinite(point.temperatureCelsius))) {
        return null;
    }

    const maxPrecip = points.reduce((max, point) => Math.max(max, point.precipitationMillimeters || 0), 0);
    const maxRisk = points.reduce((max, point) => Math.max(max, point.riskScore || 0), 0);
    const snowPoint = points.find((point) => {
        const text = `${point.description || ''} ${point.summary || ''}`.toLowerCase();
        return text.includes('snow') || text.includes('lumi') || text.includes('räntä');
    });
    const wetPoint = points.find((point) => (point.precipitationMillimeters || 0) >= 0.3);

    return {
        maxPrecip,
        maxRisk,
        hasPrecip: maxPrecip >= 0.3,
        hasSnow: Boolean(snowPoint),
        firstWetDistanceMeters: wetPoint?.distanceFromStartMeters ?? null,
        firstWetTime: wetPoint?.arrivalTime ?? null,
    };
};

const weatherChangeAlert = (previous, next) => {
    if (!previous || !next) {
        return null;
    }
    if (!previous.hasSnow && next.hasSnow) {
        return {
            level: 'high',
            title: 'Sää muuttuu: lunta tai räntää reitillä',
            message: 'Uusin säätarkistus löysi lumisateen tai räntää reitin varrelle.',
        };
    }
    if (!previous.hasPrecip && next.hasPrecip) {
        return {
            level: 'warning',
            title: 'Sää muuttuu sateiseksi',
            message: `Reitille on tulossa sadetta, enimmillään noin ${next.maxPrecip.toFixed(1)} mm.`,
        };
    }
    if (next.maxPrecip - previous.maxPrecip >= 1) {
        return {
            level: 'warning',
            title: 'Sade voimistuu reitillä',
            message: `Sademäärä nousi noin ${(next.maxPrecip - previous.maxPrecip).toFixed(1)} mm edelliseen tarkistukseen verrattuna.`,
        };
    }
    if (next.maxRisk >= 60 && previous.maxRisk < 60) {
        return {
            level: 'high',
            title: 'Ajokeli heikkenee',
            message: 'Sääriski nousi korkeaksi reitin varrella.',
        };
    }
    return null;
};

const TravelModeIcon = ({ mode }) => {
    if (mode === 'bike') {
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <circle cx="6" cy="17" r="3.2" />
                <circle cx="18" cy="17" r="3.2" />
                <path d="M8.2 17h3.1l2.1-5.2h-3.6l-1.6 5.2Z" />
                <path d="M11.3 17 7.8 11.8h5.6L18 17" />
                <path d="M13.4 11.8h2.4" />
                <path d="M10.1 9.2h2.8" />
            </svg>
        );
    }

    if (mode === 'foot') {
        return (
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M12.4 4.2a2 2 0 1 0-2.8 2.8 2 2 0 0 0 2.8-2.8Z" />
                <path d="m10.3 9.1 2.6 2.6 2.5-2.5" />
                <path d="m12.9 11.7-2.2 3.5-3.2 1.7" />
                <path d="m12.9 11.7 2.8 3.1 1.8 4" />
                <path d="M9.4 8.2 6.8 10" />
            </svg>
        );
    }

    return (
        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d="m5 12 1.8-4.1A3 3 0 0 1 9.6 6h4.8a3 3 0 0 1 2.8 1.9L19 12" />
            <path d="M4 12h16v5H4z" />
            <path d="M6 17v1.5" />
            <path d="M18 17v1.5" />
            <circle cx="7.5" cy="14.8" r="1" />
            <circle cx="16.5" cy="14.8" r="1" />
        </svg>
    );
};

const defaultDepartureTime = () => {
    const date = new Date();
    date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
    return date.toISOString().slice(0, 16);
};

const metersToKm = (meters) => `${(meters / 1000).toFixed(0)} km`;

const secondsToHours = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.round((seconds % 3600) / 60);
    return `${hours} h ${minutes} min`;
};

const progressToPercent = (progress) => `${Math.round(progress * 100)} %`;

// Kompakti jäljellä-aika navigointiin: "38 min" tai "1 h 5 min".
const remainingTimeLabel = (seconds) => {
    if (!Number.isFinite(seconds) || seconds < 0) {
        return '';
    }
    const totalMinutes = Math.max(1, Math.round(seconds / 60));
    if (totalMinutes < 60) {
        return `${totalMinutes} min`;
    }
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours} h ${minutes} min`;
};

// Jäljellä oleva matka: alle 1 km metreinä (10 m tarkkuus), muuten km.
const remainingKmLabel = (meters) => {
    if (!Number.isFinite(meters) || meters < 0) {
        return '';
    }
    if (meters < 1000) {
        return `${Math.max(0, Math.round(meters / 10) * 10)} m`;
    }
    const km = meters / 1000;
    return `${km >= 10 ? Math.round(km) : km.toFixed(1)} km`;
};

const formatSpeed = (speedKmh) => {
    if (!Number.isFinite(speedKmh)) {
        return 'Nopeus -- km/h';
    }
    return `Nopeus ${Math.max(0, Math.round(speedKmh))} km/h`;
};

// Selaimen paikannusvirheet suomeksi (raaka viesti on englanniksi, esim.
// "User denied Geolocation"). Koodit: 1 = ei lupaa, 2 = ei saatavilla, 3 = aikakatkaisu.
const geolocationErrorMessage = (positionError) => {
    switch (positionError?.code) {
        case 1:
            return 'Sijainnin jako on sallittava selaimessa, jotta paikannus toimii.';
        case 2:
            return 'Sijaintia ei saatu juuri nyt. Tarkista, että paikannus (GPS) on päällä.';
        case 3:
            return 'Sijainnin haku kesti liian kauan. Yritä uudelleen.';
        default:
            return 'Sijaintia ei saatu.';
    }
};

const coordinateDistanceMeters = (firstCoordinate, secondCoordinate) => {
    if (!firstCoordinate || !secondCoordinate) {
        return 0;
    }

    const earthRadiusMeters = 6371000;
    const firstLat = firstCoordinate[1] * Math.PI / 180;
    const secondLat = secondCoordinate[1] * Math.PI / 180;
    const deltaLat = (secondCoordinate[1] - firstCoordinate[1]) * Math.PI / 180;
    const deltaLon = (secondCoordinate[0] - firstCoordinate[0]) * Math.PI / 180;
    const a = Math.sin(deltaLat / 2) ** 2
        + Math.cos(firstLat) * Math.cos(secondLat) * Math.sin(deltaLon / 2) ** 2;
    return earthRadiusMeters * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const gpsAccuracyThresholdMeters = (accuracyMeters) => (
    OFF_ROUTE_BASE_THRESHOLD_METERS + clamp(Number.isFinite(accuracyMeters) ? accuracyMeters * 1.6 : 0, 0, 160)
);

const gpsSampleLooksUsable = (position) => {
    const accuracy = position?.coords?.accuracy;
    return !Number.isFinite(accuracy) || accuracy <= GPS_MAX_REASONABLE_ACCURACY_METERS;
};

const smoothGpsCoordinate = (previousSample, nextCoordinate, timestamp, speedKmh) => {
    if (!previousSample?.coordinate || !nextCoordinate || timestamp <= previousSample.timestamp) {
        return nextCoordinate;
    }

    const elapsedSeconds = Math.min((timestamp - previousSample.timestamp) / 1000, GPS_STALE_SAMPLE_MS / 1000);
    if (elapsedSeconds <= 0) {
        return nextCoordinate;
    }

    const distanceMeters = coordinateDistanceMeters(previousSample.coordinate, nextCoordinate);
    if (distanceMeters < 2) {
        return previousSample.coordinate;
    }

    const speedMetersPerSecond = Math.max(1.4, (Number.isFinite(speedKmh) ? speedKmh : 8) / 3.6);
    const expectedMeters = speedMetersPerSecond * elapsedSeconds;
    const followMeters = clamp(expectedMeters * 1.7 + 8, 10, 90);
    const factor = distanceMeters <= followMeters
        ? clamp(0.35 + (distanceMeters / followMeters) * 0.35, 0.35, 0.7)
        : clamp(followMeters / distanceMeters, 0.18, 0.85);

    return [
        previousSample.coordinate[0] + ((nextCoordinate[0] - previousSample.coordinate[0]) * factor),
        previousSample.coordinate[1] + ((nextCoordinate[1] - previousSample.coordinate[1]) * factor),
    ];
};

const smoothedSpeedKmh = (previousSpeedKmh, nextSpeedKmh) => {
    if (!Number.isFinite(nextSpeedKmh)) {
        return Number.isFinite(previousSpeedKmh) ? previousSpeedKmh : null;
    }
    if (!Number.isFinite(previousSpeedKmh)) {
        return nextSpeedKmh;
    }
    return (previousSpeedKmh * 0.65) + (nextSpeedKmh * 0.35);
};

const routeSpeedKmh = (route) => {
    if (!route?.distanceMeters || !route?.durationSeconds) {
        return 0;
    }
    return (route.distanceMeters / 1000) / (route.durationSeconds / 3600);
};

const routeModeWarning = (route, selectedMode, responseMode) => {
    if (!route) {
        return null;
    }

    if (responseMode && responseMode !== selectedMode) {
        return `Reittipalvelu palautti profiilin ${responseMode}, vaikka valittuna on ${selectedMode}.`;
    }

    const speedKmh = routeSpeedKmh(route);
    if (selectedMode === 'foot' && speedKmh > 8) {
        return 'Kävelyreitin aika on epärealistisen nopea. GraphHopper käyttää todennäköisesti vanhaa graph-cachea tai väärää profiilia.';
    }
    if (selectedMode === 'bike' && speedKmh > 35) {
        return 'Pyöräreitin aika näyttää liian nopealta. GraphHopper käyttää todennäköisesti vanhaa graph-cachea tai väärää profiilia.';
    }

    return null;
};

const findTailwindRouteIndex = (routes) => {
    const routesWithWind = (routes || []).filter((route) => Number.isFinite(route.averageHeadwindMetersPerSecond));
    if (!routesWithWind.length) {
        return null;
    }

    return routesWithWind.reduce((bestRoute, route) => (
        route.averageHeadwindMetersPerSecond < bestRoute.averageHeadwindMetersPerSecond ? route : bestRoute
    )).index;
};

const routeDisplayLabel = (route, tailwindRouteIndex) => {
    if (tailwindRouteIndex === null || route.index !== tailwindRouteIndex) {
        return route.label;
    }

    return route.index === 0 ? 'Nopein reitti · myötätuulisin' : 'Myötätuulisin';
};

const windArrowRotation = (windDirectionDegrees) => {
    if (!Number.isFinite(windDirectionDegrees)) {
        return 0;
    }

    return ((windDirectionDegrees + 180) * Math.PI) / 180;
};

const formatDateTime = (value) => new Date(value).toLocaleString('fi-FI', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
});

const windImpact = (headwindMetersPerSecond) => {
    if (!Number.isFinite(headwindMetersPerSecond)) {
        return null;
    }

    const rounded = Math.abs(headwindMetersPerSecond).toFixed(1);
    if (headwindMetersPerSecond >= 3) {
        return { className: 'wind-head-strong', label: `Selvä vastatuuli ${rounded} m/s` };
    }
    if (headwindMetersPerSecond >= 1) {
        return { className: 'wind-head-light', label: `Kevyt vastatuuli ${rounded} m/s` };
    }
    if (headwindMetersPerSecond <= -2) {
        return { className: 'wind-tail-strong', label: `Myötätuuli ${rounded} m/s` };
    }
    if (headwindMetersPerSecond <= -0.8) {
        return { className: 'wind-tail-light', label: `Kevyt myötätuuli ${rounded} m/s` };
    }
    return { className: 'wind-neutral', label: 'Tuuli ei juuri vaikuta' };
};

const loadStoredAuth = () => {
    try {
        const storedAuth = window.localStorage.getItem(AUTH_STORAGE_KEY);
        return storedAuth ? JSON.parse(storedAuth) : null;
    } catch (storageError) {
        return null;
    }
};

const loadStoredLastTrip = () => {
    try {
        const stored = window.localStorage.getItem(LAST_TRIP_STORAGE_KEY);
        return stored ? JSON.parse(stored) : null;
    } catch (storageError) {
        return null;
    }
};

const loadStoredNavigationMode = () => {
    try {
        // Palauta navigointitila vain jos tallennettu reitti on olemassa.
        if (!window.localStorage.getItem(LAST_TRIP_STORAGE_KEY)) {
            return 'idle';
        }
        const mode = window.localStorage.getItem(NAV_MODE_STORAGE_KEY);
        return mode === 'gps' || mode === 'running' ? mode : 'idle';
    } catch (storageError) {
        return 'idle';
    }
};

const loadStoredNavigationVoice = () => {
    try {
        return window.localStorage.getItem(NAVIGATION_VOICE_STORAGE_KEY) || 'auto';
    } catch (storageError) {
        return 'auto';
    }
};

const speedCameraCacheKey = (route) => {
    if (!route?.coordinates?.length) {
        return null;
    }

    const first = route.coordinates[0];
    const last = route.coordinates[route.coordinates.length - 1];
    return [
        route.distanceMeters,
        Math.round(first.lat * 1000),
        Math.round(first.lon * 1000),
        Math.round(last.lat * 1000),
        Math.round(last.lon * 1000),
    ].join(':');
};

const roadContextCacheKey = speedCameraCacheKey;

const routeProgressFromCoordinate = (route, coordinate) => {
    if (!route?.coordinates?.length || !coordinate) {
        return 0;
    }

    const routeCoordinates = route.coordinates.map((routeCoordinate) => fromLonLat([routeCoordinate.lon, routeCoordinate.lat]));
    const userCoordinate = fromLonLat(coordinate);
    let totalLength = 0;
    let bestDistanceSquared = Number.POSITIVE_INFINITY;
    let bestProgressDistance = 0;

    for (let index = 1; index < routeCoordinates.length; index++) {
        const previous = routeCoordinates[index - 1];
        const current = routeCoordinates[index];
        const dx = current[0] - previous[0];
        const dy = current[1] - previous[1];
        const segmentLengthSquared = dx * dx + dy * dy;
        const segmentLength = Math.sqrt(segmentLengthSquared);
        const ratio = segmentLengthSquared === 0
            ? 0
            : Math.max(0, Math.min(1, ((userCoordinate[0] - previous[0]) * dx + (userCoordinate[1] - previous[1]) * dy) / segmentLengthSquared));
        const projectedX = previous[0] + ratio * dx;
        const projectedY = previous[1] + ratio * dy;
        const distanceSquared = (userCoordinate[0] - projectedX) ** 2 + (userCoordinate[1] - projectedY) ** 2;

        if (distanceSquared < bestDistanceSquared) {
            bestDistanceSquared = distanceSquared;
            bestProgressDistance = totalLength + segmentLength * ratio;
        }

        totalLength += segmentLength;
    }

    return totalLength === 0 ? 0 : Math.max(0, Math.min(1, bestProgressDistance / totalLength));
};

// Lyhin etäisyys (metreinä) annetusta pisteestä reitin viivalle. Käytetään
// off-route-tunnistukseen: jos kuljettaja poikkeaa reitiltä, lasketaan uusi.
const distanceToRouteMeters = (route, coordinate) => {
    if (!route?.coordinates?.length || !coordinate) {
        return Number.POSITIVE_INFINITY;
    }

    if (route.coordinates.length === 1) {
        const only = route.coordinates[0];
        return coordinateDistanceMeters(coordinate, [only.lon, only.lat]);
    }

    const referenceLatRadians = coordinate[1] * Math.PI / 180;
    const metersPerDegreeLat = 111_320;
    const metersPerDegreeLon = Math.max(1, Math.cos(referenceLatRadians) * metersPerDegreeLat);
    const toLocalMeters = ([lon, lat]) => [
        (lon - coordinate[0]) * metersPerDegreeLon,
        (lat - coordinate[1]) * metersPerDegreeLat,
    ];
    const userPoint = [0, 0];
    let best = Number.POSITIVE_INFINITY;

    for (let index = 1; index < route.coordinates.length; index++) {
        const previousCoordinate = route.coordinates[index - 1];
        const currentCoordinate = route.coordinates[index];
        const previous = toLocalMeters([previousCoordinate.lon, previousCoordinate.lat]);
        const current = toLocalMeters([currentCoordinate.lon, currentCoordinate.lat]);
        const dx = current[0] - previous[0];
        const dy = current[1] - previous[1];
        const lengthSquared = dx * dx + dy * dy;
        const ratio = lengthSquared === 0
            ? 0
            : clamp(((userPoint[0] - previous[0]) * dx + (userPoint[1] - previous[1]) * dy) / lengthSquared, 0, 1);
        const projectedX = previous[0] + ratio * dx;
        const projectedY = previous[1] + ratio * dy;
        const distance = Math.sqrt((userPoint[0] - projectedX) ** 2 + (userPoint[1] - projectedY) ** 2);
        if (distance < best) {
            best = distance;
        }
    }
    return best;
};

const navigationAnimationDurationMs = (speedKmh) => {
    if (!Number.isFinite(speedKmh)) {
        return 650;
    }
    return clamp(950 - (speedKmh * 5), 450, 950);
};

const animateNavigationView = (map, coordinate, zoom, autoAnimatingRef, speedKmh = null) => {
    if (!map || !coordinate) {
        return;
    }
    autoAnimatingRef.current = true;
    const animation = { center: coordinate, duration: navigationAnimationDurationMs(speedKmh) };
    if (Number.isFinite(zoom)) {
        animation.zoom = zoom;
        animation.duration = Math.max(animation.duration, 700);
    }
    map.getView().animate(animation);
    window.setTimeout(() => {
        autoAnimatingRef.current = false;
    }, animation.duration + 150);
};

const navigationCoordinate = (route, progress, userCoordinate) => {
    if (userCoordinate) {
        return fromLonLat(userCoordinate);
    }
    if (!route?.coordinates?.length) {
        return null;
    }
    const routeCoordinates = route.coordinates.map((item) => fromLonLat([item.lon, item.lat]));
    return new LineString(routeCoordinates).getCoordinateAt(Math.min(Math.max(progress, 0), 1));
};

const roadContextLabel = (feature) => {
    if (!feature) {
        return null;
    }
    if (feature.type === 'speed_limit') {
        return feature.value ? `Nopeusrajoitus ${feature.value}` : 'Nopeusrajoitus';
    }
    return feature.label || 'Tiehuomio';
};

const instructionDistanceLabel = (meters) => {
    if (!Number.isFinite(meters)) {
        return '';
    }
    if (meters >= 1000) {
        return `${(meters / 1000).toFixed(meters >= 10_000 ? 0 : 1)} km`;
    }
    return `${Math.max(10, Math.round(meters / 10) * 10)} m`;
};

const localizeInstructionText = (text) => {
    if (!text) {
        return '';
    }

    return text
        .replace(/^Keep right$/i, 'Pysy oikealla')
        .replace(/^Keep left$/i, 'Pysy vasemmalla')
        .replace(/^Continue onto (.+)$/i, 'Jatka tielle $1')
        .replace(/^Turn right onto (.+)$/i, 'Käänny oikealle tielle $1')
        .replace(/^Turn left onto (.+)$/i, 'Käänny vasemmalle tielle $1')
        .replace(/^Turn sharp right onto (.+)$/i, 'Käänny jyrkästi oikealle tielle $1')
        .replace(/^Turn sharp left onto (.+)$/i, 'Käänny jyrkästi vasemmalle tielle $1')
        .replace(/^At roundabout, take exit (\\d+) onto (.+)$/i, 'Liikenneympyrästä poistuminen $1 tielle $2')
        .replace(/^Arrive at destination$/i, 'Saavut määränpäähän');
};

const maneuverInfo = (instruction) => {
    const sign = Number(instruction?.sign);
    const text = `${instruction?.text || ''}`.toLowerCase();

    if (text.includes('roundabout') || text.includes('liikenneympyr')) {
        return { type: 'roundabout', rotation: 0, label: 'Liikenneympyrä', laneHint: 'Valmistaudu poistumaan oikeasta kaistasta' };
    }
    if (sign === -3 || text.includes('sharp left') || text.includes('jyrkästi vasemmalle')) {
        return { type: 'turn', rotation: -90, label: 'Jyrkästi vasemmalle', laneHint: 'Ryhmittäydy vasemmalle' };
    }
    if (sign === -2 || sign === -1 || text.includes('left') || text.includes('vasemmalle')) {
        return { type: 'turn', rotation: -70, label: 'Käänny vasemmalle', laneHint: 'Ryhmittäydy vasemmalle' };
    }
    if (sign === 3 || text.includes('sharp right') || text.includes('jyrkästi oikealle')) {
        return { type: 'turn', rotation: 90, label: 'Jyrkästi oikealle', laneHint: 'Ryhmittäydy oikealle' };
    }
    if (sign === 1 || sign === 2 || text.includes('right') || text.includes('oikealle')) {
        return { type: 'turn', rotation: 70, label: 'Käänny oikealle', laneHint: 'Ryhmittäydy oikealle' };
    }
    if (text.includes('destination') || text.includes('määränpäähän')) {
        return { type: 'destination', rotation: 0, label: 'Saavut perille', laneHint: null };
    }
    return { type: 'straight', rotation: 0, label: 'Jatka suoraan', laneHint: 'Jatka nykyisellä kaistalla' };
};

const ManeuverArrow = ({ instruction }) => {
    const type = instruction?.maneuverType || 'straight';
    if (type === 'roundabout') {
        return (
            <svg className="maneuver-arrow-svg" viewBox="0 0 72 72" aria-hidden="true" focusable="false">
                <path d="M48 22a18 18 0 1 0 4 24" />
                <path d="M48 22h14V8" />
                <path d="M52 46 62 36 52 26" />
            </svg>
        );
    }
    if (type === 'destination') {
        return (
            <svg className="maneuver-arrow-svg" viewBox="0 0 72 72" aria-hidden="true" focusable="false">
                <circle cx="36" cy="36" r="18" />
                <path d="M36 18v36" />
            </svg>
        );
    }
    return (
        <svg
            className="maneuver-arrow-svg"
            viewBox="0 0 72 72"
            aria-hidden="true"
            focusable="false"
            style={{ transform: `rotate(${instruction?.rotationDegrees || 0}deg)` }}
        >
            <path d="M36 60V14" />
            <path d="M20 30 36 14l16 16" />
        </svg>
    );
};

const navigationInstruction = (route, progress) => {
    const instructions = route?.instructions;
    if (!Array.isArray(instructions) || !instructions.length || !route?.distanceMeters) {
        return null;
    }

    const traveledMeters = Math.max(0, Math.min(1, progress)) * route.distanceMeters;
    let accumulatedMeters = 0;

    for (let index = 0; index < instructions.length; index++) {
        const instruction = instructions[index];
        const instructionDistance = Number(instruction.distance) || 0;
        const instructionEnd = accumulatedMeters + instructionDistance;

        if (traveledMeters <= instructionEnd || index === instructions.length - 1) {
            const remainingToNext = Math.max(0, instructionEnd - traveledMeters);
            const nextInstruction = instructions[index + 1] || null;
            const isLast = index === instructions.length - 1;
            // Nykyinen segmentti = tie jota juuri ajetaan (kontekstiksi).
            const currentManeuver = maneuverInfo(instruction);
            // TULEVA käännös = mitä ajajan pitää tehdä seuraavaksi. Iso kortti näyttää TÄMÄN,
            // ei nykyistä tietä. (Aiemmin näytti esim. "Jatka tielle 101" vaikka pian pitää
            // kääntyä Nils Grabbes gata -tielle.)
            const upcomingManeuver = nextInstruction
                ? maneuverInfo(nextInstruction)
                : { type: 'destination', rotation: 0, label: 'Saavut perille', laneHint: null };
            const upcomingText = isLast || !nextInstruction
                ? 'Saavut määränpäähän'
                : (localizeInstructionText(nextInstruction.text) || 'Jatka reittiä');
            return {
                index,
                sign: instruction.sign,
                // Nykyinen tie (konteksti)
                currentRoadText: localizeInstructionText(instruction.text) || (isLast ? 'Saavut määränpäähän' : 'Jatka reittiä'),
                currentManeuverType: currentManeuver.type,
                // Tuleva käännös (näytetään kortissa + puhutaan)
                maneuverType: upcomingManeuver.type,
                rotationDegrees: upcomingManeuver.rotation,
                label: upcomingManeuver.label,
                laneHint: upcomingManeuver.laneHint,
                text: upcomingText,
                nextText: upcomingText,
                remainingToNext,
                remainingToDestination: Math.max(0, route.distanceMeters - traveledMeters),
            };
        }

        accumulatedMeters = instructionEnd;
    }

    return null;
};

const voiceNameIncludes = (voice, terms) => {
    const searchable = `${voice.name || ''} ${voice.lang || ''}`.toLowerCase();
    return terms.some((term) => searchable.includes(term));
};

const voiceKey = (voice) => `${voice.name || ''}|||${voice.lang || ''}`;

const voiceOptionLabel = (voice) => `${voice.name}${voice.lang ? ` (${voice.lang})` : ''}`;

const scoreNavigationVoice = (voice) => {
    const lang = (voice.lang || '').toLowerCase();
    let score = 0;

    if (lang === 'fi-fi') {
        score += 100;
    } else if (lang.startsWith('fi')) {
        score += 80;
    } else if (lang.startsWith('en')) {
        score += 20;
    }

    if (voiceNameIncludes(voice, FINNISH_NAVIGATION_VOICE_HINTS)) {
        score += 35;
    }

    if (voiceNameIncludes(voice, NATURAL_VOICE_HINTS)) {
        score += 18;
    }

    if (voiceNameIncludes(voice, SECONDARY_FEMALE_VOICE_HINTS)) {
        score += lang.startsWith('fi') ? 12 : 24;
    }

    if (voice.default) {
        score += 4;
    }

    if (voice.localService) {
        score += 2;
    }

    if (voiceNameIncludes(voice, NOVELTY_VOICE_HINTS)) {
        score -= 60;
    }

    return score;
};

const sortedNavigationVoices = (voices) => voices
    .slice()
    .sort((first, second) => scoreNavigationVoice(second) - scoreNavigationVoice(first));

const selectNavigationVoice = (preferredVoiceKey = 'auto') => {
    if (!('speechSynthesis' in window) || typeof window.speechSynthesis.getVoices !== 'function') {
        return null;
    }

    const voices = window.speechSynthesis.getVoices();
    if (!voices.length) {
        return null;
    }

    if (preferredVoiceKey !== 'auto') {
        return voices.find((voice) => voiceKey(voice) === preferredVoiceKey) || null;
    }

    const finnishVoices = voices.filter((voice) => (voice.lang || '').toLowerCase().startsWith('fi'));
    return sortedNavigationVoices(finnishVoices)[0] || null;
};

const speakInstruction = (instruction, preferredVoiceKey = 'auto', waitForVoices = true) => {
    if (!instruction?.text || !('speechSynthesis' in window)) {
        return;
    }

    const speech = window.speechSynthesis;
    if (waitForVoices && typeof speech.getVoices === 'function' && !speech.getVoices().length) {
        window.setTimeout(() => speakInstruction(instruction, preferredVoiceKey, false), 250);
        return;
    }

    speech.cancel();
    const utterance = new SpeechSynthesisUtterance(instruction.text);
    const voice = selectNavigationVoice(preferredVoiceKey);
    if (voice) {
        utterance.voice = voice;
        utterance.lang = voice.lang || 'fi-FI';
    } else {
        utterance.lang = 'fi-FI';
    }
    utterance.rate = 0.9;
    utterance.pitch = 1.04;
    speech.speak(utterance);
};

// Osoitekenttä, joka ehdottaa paikkoja backendista kirjoittaessa.
// Ehdotus hyväksytään klikkaamalla; muuten kentän saa kirjoittaa vapaasti loppuun.
const AddressInput = ({ value, onChange, placeholder }) => {
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const skipNextFetchRef = useRef(false);
    const blurTimerRef = useRef(null);

    useEffect(() => {
        if (skipNextFetchRef.current) {
            skipNextFetchRef.current = false;
            return;
        }
        const query = (value || '').trim();
        if (query.length < 3) {
            setSuggestions([]);
            return undefined;
        }
        let cancelled = false;
        const timer = setTimeout(async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/geocode/suggest`, {
                    params: { q: query },
                });
                if (!cancelled) {
                    setSuggestions(Array.isArray(response.data) ? response.data : []);
                    setOpen(true);
                }
            } catch (suggestError) {
                if (!cancelled) {
                    setSuggestions([]);
                }
            }
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [value]);

    const handleSelect = (suggestion) => {
        skipNextFetchRef.current = true;
        onChange(suggestion.label);
        setSuggestions([]);
        setOpen(false);
    };

    return (
        <div className="address-input">
            <input
                value={value}
                placeholder={placeholder}
                autoComplete="off"
                onChange={(event) => onChange(event.target.value)}
                onFocus={() => suggestions.length && setOpen(true)}
                onBlur={() => {
                    blurTimerRef.current = setTimeout(() => setOpen(false), 150);
                }}
            />
            {open && suggestions.length > 0 && (
                <ul className="address-suggestions">
                    {suggestions.map((suggestion) => (
                        <li key={`${suggestion.label}-${suggestion.lat}-${suggestion.lon}`}>
                            <button
                                type="button"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => handleSelect(suggestion)}
                            >
                                <span className="suggestion-label">{suggestion.label}</span>
                                {suggestion.displayName && (
                                    <span className="suggestion-detail">{suggestion.displayName}</span>
                                )}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const MapComponent = () => {
    const mapRef = useRef(null);
    const mapElement = useRef(null);
    const radarLayerRef = useRef(null);
    const routeLayerRef = useRef(null);
    const weatherLayerRef = useRef(null);
    const speedCameraLayerRef = useRef(null);
    const roadContextLayerRef = useRef(null);
    const digitrafficLayerRef = useRef(null);
    const fluencyLayerRef = useRef(null);
    const routeCongestionLayerRef = useRef(null);
    const chargingLayerRef = useRef(null);
    const popupRef = useRef(null);
    const popupOverlayRef = useRef(null);
    const pickRef = useRef(null);
    const pickOverlayRef = useRef(null);
    const hoveredLabelRef = useRef(null);
    const navigationModeRef = useRef('idle');
    const navigationLayerRef = useRef(null);
    const navigationTimerRef = useRef(null);
    const geolocationWatchRef = useRef(null);
    const initialLoadDoneRef = useRef(false);
    const lastTripRestoredRef = useRef(false);
    const restoringTripRef = useRef(false);
    const speedCameraCacheRef = useRef(new Map());
    const roadContextCacheRef = useRef(new Map());
    const lastSpokenInstructionRef = useRef(null);
    const lastPreparedInstructionRef = useRef(null);
    const lastGpsSampleRef = useRef(null);
    const offRouteStateRef = useRef({ count: 0, firstAt: null, maxDistance: 0 });
    const currentSpeedRef = useRef(null);
    const wakeLockRef = useRef(null);
    const navZoomedRef = useRef(false);
    const navAutoAnimatingRef = useRef(false);
    const navManualViewUntilRef = useRef(0);
    const navRestoreTimerRef = useRef(null);
    const selectedRouteRef = useRef(null);
    const navParamsRef = useRef({ end: '', departure: '', travelMode: 'car', routePreference: 'balanced' });
    const reroutingRef = useRef(false);
    const lastRerouteAtRef = useRef(0);
    const lastExecuteInstructionRef = useRef(null);
    const previousWeatherSnapshotRef = useRef(null);
    const navigationProgressRef = useRef(0);
    const userCoordinateRef = useRef(null);
    // Suurin ohjeindeksi, joka on jo ohitettu/kuulutettu – estää saman tien ("jatka
    // tietä 101") toistamisen jos GPS heilahtaa hetkeksi taaksepäin.
    const maxReachedInstructionIndexRef = useRef(-1);

    const [auth, setAuth] = useState(loadStoredAuth);
    const [authMode, setAuthMode] = useState('login');
    const [authUsername, setAuthUsername] = useState('');
    const [authPassword, setAuthPassword] = useState('');
    const [authLoading, setAuthLoading] = useState(false);
    const [authError, setAuthError] = useState(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [historyOpen, setHistoryOpen] = useState(false);
    const [whatsNewOpen, setWhatsNewOpen] = useState(false);
    const [installPrompt, setInstallPrompt] = useState(null);
    const [iosGuideOpen, setIosGuideOpen] = useState(false);
    const [attributionOpen, setAttributionOpen] = useState(false);
    const [homeAddress, setHomeAddress] = useState('');
    const [workAddress, setWorkAddress] = useState('');
    const [vehicle, setVehicle] = useState(loadStoredVehicle);
    const [editingField, setEditingField] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    // Lähtö oletuksena nykyisestä sijainnista; 'custom' = käyttäjä kirjoitti oman lähtöpaikan.
    const [startMode, setStartMode] = useState('location');
    const [travelMode, setTravelMode] = useState(loadStoredTravelMode);
    const [routePreference, setRoutePreference] = useState('balanced');
    const [advancedRouteOpen, setAdvancedRouteOpen] = useState(false);
    const [departure, setDeparture] = useState(defaultDepartureTime);
    const [tripPlan, setTripPlan] = useState(null);
    const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
    const [routeSearchOpen, setRouteSearchOpen] = useState(true);
    const [pendingAnonymousPlan, setPendingAnonymousPlan] = useState(null);
    // Ensikäynnin onboarding: kysytään oletusliikkumistapa + kotiosoite heti rekisteröinnin jälkeen.
    const [onboardingOpen, setOnboardingOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [radarEnabled, setRadarEnabled] = useState(true);
    const [radarStatus, setRadarStatus] = useState('loading');
    const [speedCamerasEnabled, setSpeedCamerasEnabled] = useState(true);
    const [speedCameraStatus, setSpeedCameraStatus] = useState('idle');
    const [speedCameraCount, setSpeedCameraCount] = useState(0);
    const [speedCameraSourceInfo, setSpeedCameraSourceInfo] = useState(SPEED_CAMERA_SOURCE_INFO);
    const [roadContextEnabled, setRoadContextEnabled] = useState(true);
    const [roadContextStatus, setRoadContextStatus] = useState('idle');
    const [roadContextFeatures, setRoadContextFeatures] = useState([]);
    const [roadContextSourceInfo, setRoadContextSourceInfo] = useState(ROAD_CONTEXT_SOURCE_INFO);
    const [digitrafficEnabled, setDigitrafficEnabled] = useState(true);
    const [digitrafficStatus, setDigitrafficStatus] = useState('idle');
    const [digitrafficItems, setDigitrafficItems] = useState([]);
    const [fluencyEnabled, setFluencyEnabled] = useState(true);
    const [fluencyStatus, setFluencyStatus] = useState('idle');
    const [fluencyItems, setFluencyItems] = useState([]);
    const [chargingEnabled, setChargingEnabled] = useState(true);
    const [chargingStatus, setChargingStatus] = useState('idle');
    const [chargingItems, setChargingItems] = useState([]);
    const [congestionTick, setCongestionTick] = useState(0);
    const [featureInfo, setFeatureInfo] = useState(null);
    // Kartalle pudotettu nasta: käyttäjä valitsee pisteen ja asettaa sen lähdöksi/kohteeksi.
    const [pickPoint, setPickPoint] = useState(null);
    const [pickAddress, setPickAddress] = useState(null);
    const [pickLoading, setPickLoading] = useState(false);
    const [trafficAdvice, setTrafficAdvice] = useState(null);
    const [navigationMode, setNavigationMode] = useState(loadStoredNavigationMode);
    const [navigationProgress, setNavigationProgress] = useState(0);
    const [userCoordinate, setUserCoordinate] = useState(null);
    const [currentSpeedKmh, setCurrentSpeedKmh] = useState(null);
    const [gpsError, setGpsError] = useState(null);
    const [weatherAlert, setWeatherAlert] = useState(null);
    const [navigationVoices, setNavigationVoices] = useState([]);
    const [navigationVoiceKey, setNavigationVoiceKey] = useState(loadStoredNavigationVoice);
    const isAuthenticated = Boolean(auth?.token && auth?.user);

    const selectedRoute = useMemo(() => {
        if (!tripPlan?.routes?.length) {
            return null;
        }
        return tripPlan.routes.find((route) => route.index === selectedRouteIndex) || tripPlan.routes[0];
    }, [tripPlan, selectedRouteIndex]);

    const currentInstruction = useMemo(
        () => navigationInstruction(selectedRoute, navigationProgress),
        [navigationProgress, selectedRoute]
    );

    // Pidä GPS-seurannan käyttämät arvot tuoreina refeissä, jotta seurantaa ei
    // tarvitse käynnistää uudelleen kun reitti vaihtuu (esim. rerouten jälkeen).
    useEffect(() => {
        selectedRouteRef.current = selectedRoute;
    }, [selectedRoute]);

    useEffect(() => {
        navigationProgressRef.current = navigationProgress;
    }, [navigationProgress]);

    useEffect(() => {
        userCoordinateRef.current = userCoordinate;
    }, [userCoordinate]);

    useEffect(() => {
        currentSpeedRef.current = currentSpeedKmh;
    }, [currentSpeedKmh]);

    useEffect(() => {
        if (!selectedRoute) {
            previousWeatherSnapshotRef.current = null;
            setWeatherAlert(null);
            return;
        }
        const snapshot = weatherSnapshot(selectedRoute);
        if (snapshot && !previousWeatherSnapshotRef.current) {
            previousWeatherSnapshotRef.current = snapshot;
        }
    }, [selectedRoute]);

    useEffect(() => {
        navParamsRef.current = { end, departure, travelMode, routePreference };
    }, [end, departure, travelMode, routePreference]);

    // Kun nasta pudotetaan kartalle: sijoita popup ja hae osoite (reverse-geokoodaus).
    useEffect(() => {
        if (!pickPoint) {
            return undefined;
        }
        pickOverlayRef.current?.setPosition(pickPoint.coordinate);
        setPickAddress(null);
        setPickLoading(true);
        let cancelled = false;
        axios.get(`${API_BASE_URL}/api/geocode/reverse`, {
            params: { lat: pickPoint.lat, lon: pickPoint.lon },
        })
            .then((response) => {
                if (!cancelled) {
                    setPickAddress(response.data || null);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setPickAddress(null);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setPickLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [pickPoint]);

    const tailwindRouteIndex = useMemo(
        () => findTailwindRouteIndex(tripPlan?.routes),
        [tripPlan]
    );

    const modeWarning = useMemo(
        () => routeModeWarning(selectedRoute, travelMode, tripPlan?.travelMode),
        [selectedRoute, travelMode, tripPlan]
    );

    const displayedSpeedKmh = navigationMode === 'gps' || navigationMode === 'tracking'
        ? currentSpeedKmh
        : navigationMode === 'running'
            ? routeSpeedKmh(selectedRoute)
            : null;

    const nextRoadFeature = useMemo(() => {
        if (!selectedRoute || (navigationMode !== 'running' && navigationMode !== 'gps')) {
            return null;
        }

        return roadContextFeatures
            .map((feature) => ({
                ...feature,
                progress: routeProgressFromCoordinate(selectedRoute, [feature.lon, feature.lat]),
            }))
            .filter((feature) => feature.progress >= navigationProgress + 0.002)
            .sort((first, second) => first.progress - second.progress)[0] || null;
    }, [navigationMode, navigationProgress, roadContextFeatures, selectedRoute]);

    // Vallitseva nopeusrajoitus GraphHopperin reittidatasta (kattaa koko reitin):
    // valitaan viimeisin osuus, jonka alku on jo ohitettu nykyisellä edistymällä.
    const currentSpeedLimit = useMemo(() => {
        if (!selectedRoute || (navigationMode !== 'running' && navigationMode !== 'gps')) {
            return null;
        }
        const limits = selectedRoute.speedLimits;
        if (!Array.isArray(limits) || !limits.length) {
            return null;
        }
        const currentMeters = navigationProgress * selectedRoute.distanceMeters;
        let current = null;
        for (const segment of limits) {
            if (segment.distanceFromStartMeters <= currentMeters + 5) {
                current = segment;
            } else {
                break;
            }
        }
        return (current || limits[0]).speedKmh ?? null;
    }, [navigationMode, navigationProgress, selectedRoute]);

    // Ylinopeus, kun mitattu nopeus ylittää rajoituksen selvästi (GPS-kohinan varalta +5 km/h).
    const isSpeeding = Number.isFinite(displayedSpeedKmh)
        && Number.isFinite(currentSpeedLimit)
        && displayedSpeedKmh > currentSpeedLimit + 5;

    // Saapumisennuste: jäljellä olevat kilometrit, arvioitu aika ja kellonaika perillä.
    // Perusarvio nojaa reitin omaan kesto-/matkaprofiiliin (tasainen), mutta jos ajetaan
    // ja nykyvauhti tiedetään, sekoitetaan se mukaan jotta ETA reagoi todelliseen vauhtiin.
    const navEta = useMemo(() => {
        if ((navigationMode !== 'gps' && navigationMode !== 'running') || !selectedRoute?.distanceMeters) {
            return null;
        }
        const remainingMeters = currentInstruction
            ? currentInstruction.remainingToDestination
            : Math.max(0, (1 - navigationProgress) * selectedRoute.distanceMeters);
        const byRouteSeconds = selectedRoute.durationSeconds
            ? (remainingMeters / selectedRoute.distanceMeters) * selectedRoute.durationSeconds
            : null;
        let remainingSeconds = byRouteSeconds;
        if (Number.isFinite(displayedSpeedKmh) && displayedSpeedKmh > 5) {
            const bySpeedSeconds = remainingMeters / (displayedSpeedKmh / 3.6);
            remainingSeconds = Number.isFinite(byRouteSeconds)
                ? byRouteSeconds * 0.6 + bySpeedSeconds * 0.4
                : bySpeedSeconds;
        }
        if (!Number.isFinite(remainingSeconds)) {
            return null;
        }
        const arrival = new Date(Date.now() + remainingSeconds * 1000);
        return {
            remainingMeters,
            remainingSeconds,
            arrivalLabel: arrival.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' }),
        };
    }, [navigationMode, selectedRoute, currentInstruction, navigationProgress, displayedSpeedKmh]);

    const fetchTripPlan = useCallback(async (startValue, endValue, departureValue, modeValue, preferenceValue, authOverride = null, options = {}) => {
        const keepNavigating = options.keepNavigating === true;
        const background = options.background === true;
        const weatherRefresh = options.weatherRefresh === true;
        // Älä hae reittiä ennen kuin sekä lähtö että kohde on annettu.
        if (!startValue?.trim() || !endValue?.trim()) {
            if (!keepNavigating) {
                setError('Anna sekä lähtöpaikka että kohde.');
            }
            return;
        }
        if (!background) {
            setLoading(true);
            setError(null);
        }
        const requestAuth = authOverride || auth;

        const requestOptions = {
            params: {
                start: startValue,
                end: endValue,
                mode: modeValue,
                preference: preferenceValue,
                departure: departureValue,
            },
        };

        if (requestAuth?.token) {
            requestOptions.headers = {
                Authorization: `Bearer ${requestAuth.token}`,
            };
        }

        try {
            const response = await axios.get(`${API_BASE_URL}/api/trip/plan`, requestOptions);
            const nextRoutes = response.data.routes || [];
            // Rerouten yhteydessä navigointi jatkuu: estä reitinvaihdon nollausefektiä
            // (joka muuten pysäyttäisi navigoinnin ja sammuttaisi GPS-seurannan).
            if (keepNavigating) {
                restoringTripRef.current = true;
            }
            setTripPlan(response.data);
            const currentRouteIndex = selectedRouteRef.current?.index;
            const nextSelectedRoute = nextRoutes.find((route) => route.index === currentRouteIndex) || nextRoutes[0];
            setSelectedRouteIndex(nextSelectedRoute?.index ?? 0);
            if (!background) {
                previousWeatherSnapshotRef.current = weatherSnapshot(nextSelectedRoute);
                setWeatherAlert(null);
                setRouteSearchOpen(false);
                setNavigationProgress(0);
            }
            // Rerouten yhteydessä navigointi jatkuu; muuten palataan idleen.
            if (!keepNavigating && !background) {
                setNavigationMode('idle');
            }
            if (weatherRefresh) {
                const nextSnapshot = weatherSnapshot(nextSelectedRoute);
                const alert = weatherChangeAlert(previousWeatherSnapshotRef.current, nextSnapshot);
                if (alert) {
                    setWeatherAlert({ ...alert, checkedAt: new Date().toISOString() });
                }
                if (nextSnapshot) {
                    previousWeatherSnapshotRef.current = nextSnapshot;
                }
            }
            // Muista viimeisin reitti paluuta varten (myös anonyymisti).
            try {
                window.localStorage.setItem(LAST_TRIP_STORAGE_KEY, JSON.stringify({
                    plan: response.data,
                    start: startValue,
                    end: endValue,
                    departure: departureValue,
                    mode: modeValue,
                    preference: preferenceValue,
                }));
            } catch (storageError) {
                // localStorage ei käytettävissä – ohitetaan hiljaisesti.
            }
        } catch (requestError) {
            if (!background) {
                setError(requestError.response?.data?.message || requestError.message);
            }
        } finally {
            if (!background) {
                setLoading(false);
            }
        }
    }, [auth]);

    const applyStoredTrip = useCallback((stored) => {
        if (!stored?.plan?.routes?.length) {
            return;
        }
        // Estä navigoinnin nollaus, kun reitti palautetaan (reloadin jälkeen).
        restoringTripRef.current = true;
        if (stored.start) setStart(stored.start);
        if (stored.end) setEnd(stored.end);
        if (stored.departure) setDeparture(stored.departure);
        if (stored.mode) setTravelMode(stored.mode);
        if (stored.preference) setRoutePreference(stored.preference);
        setTripPlan(stored.plan);
        setSelectedRouteIndex(stored.plan.routes[0]?.index ?? 0);
        setRouteSearchOpen(false);
    }, []);

    const drawTripPlan = useCallback(() => {
        if (!mapRef.current || !routeLayerRef.current || !weatherLayerRef.current || !tripPlan?.routes) {
            return;
        }

        const routeSource = routeLayerRef.current.getSource();
        const weatherSource = weatherLayerRef.current.getSource();
        routeSource.clear();
        weatherSource.clear();

        tripPlan.routes.forEach((route) => {
            const routeCoordinates = route.coordinates.map((coordinate) => fromLonLat([coordinate.lon, coordinate.lat]));
            if (routeCoordinates.length > 1) {
                const routeFeature = new Feature({
                    geometry: new LineString(routeCoordinates),
                    routeIndex: route.index,
                    selected: route.index === selectedRouteIndex,
                });
                routeSource.addFeature(routeFeature);
            }
        });

        const activeRoute = tripPlan.routes.find((route) => route.index === selectedRouteIndex) || tripPlan.routes[0];
        activeRoute.weatherPoints.forEach((weatherPoint) => {
            const weatherFeature = new Feature({
                geometry: new Point(fromLonLat([weatherPoint.lon, weatherPoint.lat])),
                riskLevel: weatherPoint.riskLevel,
                windDirectionDegrees: weatherPoint.windDirectionDegrees,
                windSpeedMetersPerSecond: weatherPoint.windSpeedMetersPerSecond,
            });
            weatherSource.addFeature(weatherFeature);
        });

        const extent = routeSource.getExtent();
        if (extent && Number.isFinite(extent[0])) {
            mapRef.current.getView().fit(extent, {
                padding: [40, 40, 40, 40],
                maxZoom: 10,
                duration: 300,
            });
        }

        routeLayerRef.current.changed();
    }, [selectedRouteIndex, tripPlan]);

    const drawNavigationMarker = useCallback(() => {
        if (!navigationLayerRef.current) {
            return;
        }

        const navigationSource = navigationLayerRef.current.getSource();
        navigationSource.clear();

        if (navigationMode === 'tracking') {
            if (!userCoordinate) {
                return;
            }
            const markerCoordinate = fromLonLat(userCoordinate);
            navigationSource.addFeature(new Feature({
                geometry: new Point(markerCoordinate),
                heading: 0,
            }));
            if (mapRef.current) {
                const now = Date.now();
                if (now < navManualViewUntilRef.current) {
                    return;
                }
                if (navManualViewUntilRef.current !== 0 && now >= navManualViewUntilRef.current) {
                    navManualViewUntilRef.current = 0;
                    animateNavigationView(mapRef.current, markerCoordinate, 16.5, navAutoAnimatingRef, currentSpeedKmh);
                    navZoomedRef.current = true;
                    return;
                }
                if (!navZoomedRef.current) {
                    animateNavigationView(mapRef.current, markerCoordinate, 16.5, navAutoAnimatingRef, currentSpeedKmh);
                    navZoomedRef.current = true;
                } else {
                    animateNavigationView(mapRef.current, markerCoordinate, null, navAutoAnimatingRef, currentSpeedKmh);
                }
            }
            return;
        }

        if (!selectedRoute?.coordinates?.length || navigationMode === 'idle') {
            return;
        }

        const routeCoordinates = selectedRoute.coordinates.map((coordinate) => fromLonLat([coordinate.lon, coordinate.lat]));
        const routeLine = new LineString(routeCoordinates);
        const progressAt = Math.min(Math.max(navigationProgress, 0), 1);

        let markerCoordinate = userCoordinate ? fromLonLat(userCoordinate) : routeLine.getCoordinateAt(progressAt);

        // Kulkusuunta tien myötäisesti: piste hieman edempänä reitillä.
        const aheadCoordinate = routeLine.getCoordinateAt(Math.min(progressAt + 0.01, 1));
        const behindCoordinate = routeLine.getCoordinateAt(progressAt);
        const dx = aheadCoordinate[0] - behindCoordinate[0];
        const dy = aheadCoordinate[1] - behindCoordinate[1];
        const heading = (dx === 0 && dy === 0) ? 0 : Math.atan2(dx, dy);

        const navigationFeature = new Feature({
            geometry: new Point(markerCoordinate),
            heading,
        });
        navigationSource.addFeature(navigationFeature);

        if ((navigationMode === 'running' || navigationMode === 'gps') && mapRef.current) {
            const now = Date.now();
            if (now < navManualViewUntilRef.current) {
                return;
            }
            if (navManualViewUntilRef.current !== 0 && now >= navManualViewUntilRef.current) {
                navManualViewUntilRef.current = 0;
                animateNavigationView(mapRef.current, markerCoordinate, 16.5, navAutoAnimatingRef, currentSpeedKmh);
                navZoomedRef.current = true;
                return;
            }
            if (!navZoomedRef.current) {
                // Navigoinnin alussa: zoomaa kerran lähelle nykyistä ympäristöä.
                animateNavigationView(mapRef.current, markerCoordinate, 16.5, navAutoAnimatingRef, currentSpeedKmh);
                navZoomedRef.current = true;
            } else {
                // Seuranta: keskitä liikkeen mukaan, mutta älä pakota zoomia (käyttäjä saa zoomata).
                animateNavigationView(mapRef.current, markerCoordinate, null, navAutoAnimatingRef, currentSpeedKmh);
            }
        }
    }, [currentSpeedKmh, navigationMode, navigationProgress, selectedRoute, userCoordinate]);

    useEffect(() => {
        if (mapRef.current || !mapElement.current) {
            return;
        }

        radarLayerRef.current = new TileLayer({
            opacity: 0.58,
            maxZoom: RAIN_RADAR_MAX_ZOOM,
            visible: true,
        });

        routeLayerRef.current = new VectorLayer({
            source: new VectorSource(),
            style: (feature) => {
                const routeIndex = feature.get('routeIndex') || 0;
                const isSelected = feature.get('selected');
                const navigating = navigationModeRef.current === 'gps' || navigationModeRef.current === 'running';
                return new Style({
                    stroke: new Stroke({
                        // Navigoitaessa valittu ajoreitti vaaleansiniseksi (erottuu vihreästä maastosta; ruuhkaosuudet värjätään päälle).
                        color: navigating && isSelected ? '#38bdf8' : routeColors[routeIndex % routeColors.length],
                        width: isSelected ? 6 : 3,
                        lineDash: isSelected ? undefined : [8, 10],
                    }),
                });
            },
        });

        weatherLayerRef.current = new VectorLayer({
            source: new VectorSource(),
            style: (feature) => {
                const riskLevel = feature.get('riskLevel') || 'UNKNOWN';
                const windDirectionDegrees = feature.get('windDirectionDegrees');
                const windSpeedMetersPerSecond = feature.get('windSpeedMetersPerSecond');
                const styles = [
                    new Style({
                        image: new CircleStyle({
                            radius: 7,
                            fill: new Fill({ color: riskColors[riskLevel] || riskColors.UNKNOWN }),
                            stroke: new Stroke({ color: '#ffffff', width: 2 }),
                        }),
                    }),
                ];

                if (Number.isFinite(windDirectionDegrees)) {
                    styles.push(new Style({
                        image: new Icon({
                            src: windIconSrc,
                            scale: 0.62,
                            anchor: [0.5, 1.3],
                            rotation: windArrowRotation(windDirectionDegrees),
                            rotateWithView: true,
                        }),
                    }));
                }

                if (Number.isFinite(windSpeedMetersPerSecond)) {
                    styles.push(new Style({
                        text: new Text({
                            text: `${Number(windSpeedMetersPerSecond).toFixed(1)} m/s`,
                            offsetY: -39,
                            fill: new Fill({ color: '#0f172a' }),
                            stroke: new Stroke({ color: '#ffffff', width: 4 }),
                            font: '800 10px sans-serif',
                        }),
                    }));
                }

                return styles;
            },
        });

        speedCameraLayerRef.current = new VectorLayer({
            source: new VectorSource(),
            visible: true,
            style: (feature) => {
                const maxspeed = feature.get('maxspeed') || '';
                return new Style({
                    image: new CircleStyle({
                        radius: maxspeed ? 13 : 10,
                        fill: new Fill({ color: '#b91c1c' }),
                        stroke: new Stroke({ color: '#ffffff', width: 3 }),
                    }),
                    text: maxspeed
                        ? new Text({
                            text: String(maxspeed).replace(/[^\d]/g, '').slice(0, 3),
                            fill: new Fill({ color: '#ffffff' }),
                            font: '700 10px sans-serif',
                        })
                        : undefined,
                });
            },
        });

        roadContextLayerRef.current = new VectorLayer({
            source: new VectorSource(),
            visible: true,
            style: (feature) => {
                const type = feature.get('type');
                const value = feature.get('value') || '';
                const speedText = String(value).replace(/[^\d]/g, '').slice(0, 3);
                const isSpeedLimit = type === 'speed_limit';
                const symbol = type === 'traffic_signals'
                    ? '●'
                    : type === 'stop'
                        ? 'STOP'
                        : type === 'give_way'
                            ? '△'
                            : type === 'crossing'
                                ? '↕'
                                : '!';

                return new Style({
                    image: new CircleStyle({
                        radius: isSpeedLimit ? 14 : 11,
                        fill: new Fill({ color: isSpeedLimit ? '#ffffff' : '#f97316' }),
                        stroke: new Stroke({ color: isSpeedLimit ? '#172033' : '#ffffff', width: isSpeedLimit ? 3 : 2 }),
                    }),
                    text: new Text({
                        text: isSpeedLimit ? speedText : symbol,
                        fill: new Fill({ color: isSpeedLimit ? '#172033' : '#ffffff' }),
                        font: isSpeedLimit ? '800 10px sans-serif' : '900 9px sans-serif',
                    }),
                });
            },
        });

        digitrafficLayerRef.current = new VectorLayer({
            source: new VectorSource(),
            visible: true,
            style: (feature) => {
                const isRoadwork = feature.get('type') === 'roadwork';
                return new Style({
                    image: new CircleStyle({
                        radius: 11,
                        fill: new Fill({ color: isRoadwork ? '#eab308' : '#dc2626' }),
                        stroke: new Stroke({ color: '#ffffff', width: 2 }),
                    }),
                    text: new Text({
                        text: isRoadwork ? 'T' : '!',
                        fill: new Fill({ color: '#ffffff' }),
                        font: '900 11px sans-serif',
                    }),
                });
            },
        });

        fluencyLayerRef.current = new VectorLayer({
            source: new VectorSource(),
            visible: true,
            style: (feature) => {
                const percent = feature.get('percent');
                const speed = feature.get('speed');
                const color = percent == null
                    ? '#94a3b8'
                    : percent >= 85
                        ? '#16a34a'
                        : percent >= 60
                            ? '#eab308'
                            : '#dc2626';
                return new Style({
                    image: new CircleStyle({
                        radius: 12,
                        fill: new Fill({ color }),
                        stroke: new Stroke({ color: '#ffffff', width: 2 }),
                    }),
                    text: new Text({
                        text: Number.isFinite(speed) ? String(Math.round(speed)) : '',
                        fill: new Fill({ color: '#ffffff' }),
                        font: '800 10px sans-serif',
                    }),
                });
            },
        });

        routeCongestionLayerRef.current = new VectorLayer({
            source: new VectorSource(),
            visible: true,
            style: (feature) => new Style({
                stroke: new Stroke({
                    color: feature.get('band') === 'red' ? '#dc2626' : '#f97316',
                    width: 7,
                }),
            }),
        });

        chargingLayerRef.current = new VectorLayer({
            source: new VectorSource(),
            visible: true,
            style: (feature) => {
                const fast = feature.get('fast');
                const power = feature.get('power');
                return new Style({
                    image: new CircleStyle({
                        radius: 11,
                        fill: new Fill({ color: fast ? '#16a34a' : '#0d9488' }),
                        stroke: new Stroke({ color: '#ffffff', width: 2 }),
                    }),
                    text: new Text({
                        text: Number.isFinite(power) && power > 0 ? String(power) : '⚡',
                        fill: new Fill({ color: '#ffffff' }),
                        font: '800 9px sans-serif',
                    }),
                });
            },
        });

        navigationLayerRef.current = new VectorLayer({
            source: new VectorSource(),
            style: (feature) => new Style({
                // Suuntakolmio joka kääntyy kulkusuuntaan (tien myötäisesti).
                image: new RegularShape({
                    points: 3,
                    radius: 12,
                    fill: new Fill({ color: '#2563eb' }),
                    stroke: new Stroke({ color: '#ffffff', width: 3 }),
                    rotation: feature.get('heading') || 0,
                    rotateWithView: true,
                }),
            }),
        });

        mapRef.current = new Map({
            target: mapElement.current,
            layers: [
                new TileLayer({ source: new OSM() }),
                radarLayerRef.current,
                routeLayerRef.current,
                routeCongestionLayerRef.current,
                weatherLayerRef.current,
                roadContextLayerRef.current,
                speedCameraLayerRef.current,
                digitrafficLayerRef.current,
                fluencyLayerRef.current,
                chargingLayerRef.current,
                navigationLayerRef.current,
            ],
            view: new View({
                center: fromLonLat([25.7, 61.4]),
                zoom: 7,
                minZoom: MAP_MIN_ZOOM,
                maxZoom: MAP_MAX_ZOOM,
                enableRotation: false,
            }),
            // Oletusattribuutio pois – korvataan omalla koostetulla ⓘ-napilla.
            controls: defaultControls({ attribution: false }),
        });

        if (popupRef.current) {
            popupOverlayRef.current = new Overlay({
                element: popupRef.current,
                positioning: 'bottom-center',
                offset: [0, -18],
                stopEvent: true,
            });
            mapRef.current.addOverlay(popupOverlayRef.current);
        }

        if (pickRef.current) {
            pickOverlayRef.current = new Overlay({
                element: pickRef.current,
                positioning: 'bottom-center',
                offset: [0, -14],
                stopEvent: true,
            });
            mapRef.current.addOverlay(pickOverlayRef.current);
        }

        const handleMapClick = (event) => {
            let clickedRouteIndex = null;
            let infoLabel = null;
            let infoCoordinate = null;
            mapRef.current.forEachFeatureAtPixel(event.pixel, (feature) => {
                const routeIndex = feature.get('routeIndex');
                if (Number.isFinite(routeIndex)) {
                    clickedRouteIndex = routeIndex;
                    return true;
                }
                const label = feature.get('label');
                if (!infoLabel && label) {
                    infoLabel = label;
                    const geometry = feature.getGeometry();
                    infoCoordinate = geometry && geometry.getCoordinates ? geometry.getCoordinates() : null;
                }
                return false;
            }, { hitTolerance: 8 });

            if (clickedRouteIndex !== null) {
                setSelectedRouteIndex(clickedRouteIndex);
            }

            if (infoLabel && infoCoordinate && popupOverlayRef.current) {
                setFeatureInfo({ text: infoLabel });
                popupOverlayRef.current.setPosition(infoCoordinate);
                return;
            }

            setFeatureInfo(null);
            popupOverlayRef.current?.setPosition(undefined);

            // Tyhjään kohtaan klikkaus riittävän lähellä (zoom ≥ 15): pudota nasta,
            // josta voi asettaa pisteen lähdöksi tai kohteeksi (talonumerotarkkuus).
            if (clickedRouteIndex === null && navigationModeRef.current === 'idle') {
                const zoom = mapRef.current.getView().getZoom() || 0;
                if (zoom >= 15) {
                    const [lon, lat] = toLonLat(event.coordinate);
                    setPickPoint({ coordinate: event.coordinate, lon, lat });
                } else {
                    setPickPoint(null);
                    pickOverlayRef.current?.setPosition(undefined);
                }
            }
        };

        const updatePointer = (event) => {
            if (event.dragging) {
                return;
            }
            // Kosketuksella käytetään klikkausta (ei hoveria).
            if (event.originalEvent && event.originalEvent.pointerType === 'touch') {
                return;
            }

            let label = null;
            let coordinate = null;
            let routeHit = false;
            mapRef.current.forEachFeatureAtPixel(event.pixel, (feature) => {
                if (Number.isFinite(feature.get('routeIndex'))) {
                    routeHit = true;
                    return false;
                }
                const featureLabel = feature.get('label');
                if (featureLabel && !label) {
                    label = featureLabel;
                    const geometry = feature.getGeometry();
                    coordinate = geometry && geometry.getCoordinates ? geometry.getCoordinates() : null;
                }
                return false;
            }, { hitTolerance: 8 });

            mapElement.current.style.cursor = (label || routeHit) ? 'pointer' : '';

            // Näytä popup hoverilla; päivitä tila vain kun kohde vaihtuu.
            if (label && coordinate && popupOverlayRef.current) {
                if (hoveredLabelRef.current !== label) {
                    hoveredLabelRef.current = label;
                    setFeatureInfo({ text: label });
                }
                popupOverlayRef.current.setPosition(coordinate);
            } else if (hoveredLabelRef.current !== null) {
                hoveredLabelRef.current = null;
                setFeatureInfo(null);
                popupOverlayRef.current?.setPosition(undefined);
            }
        };

        const restoreNavigationView = () => {
            navManualViewUntilRef.current = 0;
            navRestoreTimerRef.current = null;
            const mode = navigationModeRef.current;
            const coordinate = navigationCoordinate(selectedRouteRef.current, navigationProgressRef.current, userCoordinateRef.current);
            if (!coordinate || !mapRef.current || (mode !== 'gps' && mode !== 'running' && mode !== 'tracking')) {
                return;
            }
            animateNavigationView(mapRef.current, coordinate, 16.5, navAutoAnimatingRef, currentSpeedRef.current);
            navZoomedRef.current = true;
        };

        const scheduleNavigationViewRestore = () => {
            const mode = navigationModeRef.current;
            if (mode !== 'gps' && mode !== 'running' && mode !== 'tracking') {
                return;
            }
            if (navAutoAnimatingRef.current) {
                return;
            }

            navManualViewUntilRef.current = Date.now() + NAV_VIEW_RESTORE_DELAY_MS;
            if (navRestoreTimerRef.current) {
                window.clearTimeout(navRestoreTimerRef.current);
            }
            navRestoreTimerRef.current = window.setTimeout(restoreNavigationView, NAV_VIEW_RESTORE_DELAY_MS);
        };

        const view = mapRef.current.getView();
        const handleManualZoomChange = () => scheduleNavigationViewRestore();

        mapRef.current.on('singleclick', handleMapClick);
        mapRef.current.on('pointermove', updatePointer);
        mapRef.current.on('moveend', scheduleNavigationViewRestore);
        view.on('change:resolution', handleManualZoomChange);

        requestAnimationFrame(() => {
            mapRef.current?.updateSize();
        });

        return () => {
            mapRef.current?.un('singleclick', handleMapClick);
            mapRef.current?.un('pointermove', updatePointer);
            mapRef.current?.un('moveend', scheduleNavigationViewRestore);
            view.un('change:resolution', handleManualZoomChange);
            if (navRestoreTimerRef.current) {
                window.clearTimeout(navRestoreTimerRef.current);
                navRestoreTimerRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        drawTripPlan();
    }, [drawTripPlan]);

    useEffect(() => {
        drawNavigationMarker();
    }, [drawNavigationMarker]);

    useEffect(() => {
        if (radarLayerRef.current) {
            const zoom = mapRef.current?.getView().getZoom() ?? 0;
            radarLayerRef.current.setVisible(radarEnabled && isAuthenticated && zoom <= RAIN_RADAR_MAX_ZOOM);
        }
    }, [isAuthenticated, radarEnabled]);

    useEffect(() => {
        const view = mapRef.current?.getView();
        if (!view) {
            return undefined;
        }

        const updateRadarVisibility = () => {
            const zoom = view.getZoom() ?? 0;
            radarLayerRef.current?.setVisible(radarEnabled && isAuthenticated && zoom <= RAIN_RADAR_MAX_ZOOM);
        };

        view.on('change:resolution', updateRadarVisibility);
        updateRadarVisibility();
        return () => view.un('change:resolution', updateRadarVisibility);
    }, [isAuthenticated, radarEnabled]);

    // Näytä "mitä uutta" -kooste kerran, kun sovellusversio on vaihtunut.
    useEffect(() => {
        let seen = null;
        try {
            seen = window.localStorage.getItem(SEEN_VERSION_STORAGE_KEY);
        } catch (storageError) {
            seen = null;
        }
        if (seen !== APP_VERSION) {
            setWhatsNewOpen(true);
        }
    }, []);

    const dismissWhatsNew = useCallback(() => {
        setWhatsNewOpen(false);
        try {
            window.localStorage.setItem(SEEN_VERSION_STORAGE_KEY, APP_VERSION);
        } catch (storageError) {
            /* ignore */
        }
    }, []);

    // Kotivalikkoon asennus: Chrome/Android tarjoaa beforeinstallprompt-tapahtuman,
    // jonka voi laukaista omalla napilla. iOS/Safari ei salli ohjelmallista asennusta.
    useEffect(() => {
        const handleBeforeInstall = (event) => {
            event.preventDefault();
            setInstallPrompt(event);
        };
        const handleInstalled = () => setInstallPrompt(null);
        window.addEventListener('beforeinstallprompt', handleBeforeInstall);
        window.addEventListener('appinstalled', handleInstalled);
        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
            window.removeEventListener('appinstalled', handleInstalled);
        };
    }, []);

    const isStandalone = typeof window !== 'undefined'
        && (window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true);
    const isIosDevice = typeof navigator !== 'undefined'
        && /iphone|ipad|ipod/i.test(navigator.userAgent)
        && !window.MSStream;

    const handleInstallApp = useCallback(async () => {
        if (!installPrompt) {
            return;
        }
        installPrompt.prompt();
        try {
            await installPrompt.userChoice;
        } catch (choiceError) {
            /* käyttäjä sulki – ei tehdä mitään */
        }
        setInstallPrompt(null);
    }, [installPrompt]);

    const dismissIosGuide = useCallback(() => {
        setIosGuideOpen(false);
        try {
            window.localStorage.setItem(IOS_INSTALL_HINT_STORAGE_KEY, '1');
        } catch (storageError) {
            /* ignore */
        }
    }, []);

    // Näytä iOS-Safarissa kerran ohje kotivalikkoon lisäämisestä (ei standalone-tilassa).
    useEffect(() => {
        if (!isIosDevice || isStandalone) {
            return undefined;
        }
        let seen = null;
        try {
            seen = window.localStorage.getItem(IOS_INSTALL_HINT_STORAGE_KEY);
        } catch (storageError) {
            seen = null;
        }
        if (seen) {
            return undefined;
        }
        const timer = setTimeout(() => setIosGuideOpen(true), 2500);
        return () => clearTimeout(timer);
    }, [isIosDevice, isStandalone]);

    useEffect(() => {
        if (speedCameraLayerRef.current) {
            speedCameraLayerRef.current.setVisible(speedCamerasEnabled);
        }
    }, [speedCamerasEnabled]);

    useEffect(() => {
        if (roadContextLayerRef.current) {
            roadContextLayerRef.current.setVisible(roadContextEnabled);
        }
    }, [roadContextEnabled]);

    useEffect(() => {
        if (digitrafficLayerRef.current) {
            digitrafficLayerRef.current.setVisible(digitrafficEnabled);
        }
    }, [digitrafficEnabled]);

    useEffect(() => {
        if (fluencyLayerRef.current) {
            fluencyLayerRef.current.setVisible(fluencyEnabled);
        }
    }, [fluencyEnabled]);

    useEffect(() => {
        if (chargingLayerRef.current) {
            chargingLayerRef.current.setVisible(chargingEnabled);
        }
    }, [chargingEnabled]);

    useEffect(() => {
        if (!auth?.token || !tripPlan?.weatherEnabled || !selectedRoute || !start.trim() || !end.trim()) {
            return undefined;
        }

        const timer = window.setInterval(() => {
            fetchTripPlan(start, end, departure, travelMode, routePreference, null, {
                background: true,
                weatherRefresh: true,
            });
        }, WEATHER_POLL_INTERVAL_MS);

        return () => window.clearInterval(timer);
    }, [
        auth?.token,
        departure,
        end,
        fetchTripPlan,
        routePreference,
        selectedRoute,
        start,
        travelMode,
        tripPlan?.weatherEnabled,
    ]);

    // Laskee uuden reitin nykysijainnista määränpäähän (navigointi jatkuu).
    // Kutsutaan kun kuljettaja poikkeaa valitulta reitiltä.
    const rerouteFromCurrent = useCallback((coordinate) => {
        const now = Date.now();
        if (reroutingRef.current || now - lastRerouteAtRef.current < 12000) {
            return;
        }
        const params = navParamsRef.current;
        if (!params.end) {
            return;
        }
        const lon = coordinate[0].toFixed(5);
        const lat = coordinate[1].toFixed(5);
        reroutingRef.current = true;
        lastRerouteAtRef.current = now;
        lastSpokenInstructionRef.current = null;
        maxReachedInstructionIndexRef.current = -1;
        lastPreparedInstructionRef.current = null;
        lastExecuteInstructionRef.current = null;
        setStart(`${lat},${lon}`);
        speakInstruction({ text: 'Poikkesit reitiltä. Lasketaan uusi reitti.' }, navigationVoiceKey);
        fetchTripPlan(`${lat},${lon}`, params.end, params.departure, params.travelMode, params.routePreference, null, { keepNavigating: true })
            .finally(() => {
                reroutingRef.current = false;
            });
    }, [fetchTripPlan, navigationVoiceKey]);

    const rerouteFromCurrentRef = useRef(rerouteFromCurrent);
    useEffect(() => {
        rerouteFromCurrentRef.current = rerouteFromCurrent;
    }, [rerouteFromCurrent]);

    // Käynnistää GPS-seurannan (käytetään sekä "Aloita"-napista että navigoinnin
    // palautuksessa reloadin jälkeen). Käyttää refejä, joten seurantaa ei tarvitse
    // käynnistää uudelleen reitin vaihtuessa (esim. rerouten jälkeen).
    const startGpsWatch = useCallback((mode = 'gps') => {
        if (!navigator.geolocation) {
            return;
        }
        if (geolocationWatchRef.current !== null) {
            navigator.geolocation.clearWatch(geolocationWatchRef.current);
        }
        offRouteStateRef.current = { count: 0, firstAt: null, maxDistance: 0 };
        currentSpeedRef.current = null;
        geolocationWatchRef.current = navigator.geolocation.watchPosition(
            (position) => {
                const rawCoordinate = [position.coords.longitude, position.coords.latitude];
                const timestamp = position.timestamp || Date.now();
                const measuredSpeedKmh = Number.isFinite(position.coords.speed)
                    ? position.coords.speed * 3.6
                    : null;
                const previousSample = lastGpsSampleRef.current;
                const previousRawCoordinate = previousSample?.rawCoordinate || previousSample?.coordinate;
                const calculatedSpeedKmh = previousRawCoordinate && timestamp > previousSample.timestamp
                    ? (coordinateDistanceMeters(previousRawCoordinate, rawCoordinate) / ((timestamp - previousSample.timestamp) / 1000)) * 3.6
                    : null;
                const nextSpeedKmh = smoothedSpeedKmh(
                    currentSpeedRef.current,
                    Number.isFinite(measuredSpeedKmh) ? measuredSpeedKmh : calculatedSpeedKmh
                );

                if (!gpsSampleLooksUsable(position) && previousSample?.coordinate) {
                    offRouteStateRef.current = { count: 0, firstAt: null, maxDistance: 0 };
                    // Epäluotettava fix: älä hyväksy sitä sijainniksi. Dead reckoning -intervalli
                    // jatkaa matkaa reittiä pitkin, joten auto ei jää paikalleen.
                    setGpsError('Heikko GPS-signaali – jatketaan reittiä arvioidulla vauhdilla.');
                    setCurrentSpeedKmh(nextSpeedKmh);
                    setNavigationMode(selectedRouteRef.current && mode !== 'tracking' ? 'gps' : 'tracking');
                    return;
                }

                const nextCoordinate = smoothGpsCoordinate(previousSample, rawCoordinate, timestamp, nextSpeedKmh);
                lastGpsSampleRef.current = { coordinate: nextCoordinate, rawCoordinate, timestamp };
                setCurrentSpeedKmh(nextSpeedKmh);
                setUserCoordinate(nextCoordinate);
                setGpsError(null);

                const route = selectedRouteRef.current;
                if (route && mode !== 'tracking') {
                    setNavigationProgress(routeProgressFromCoordinate(route, nextCoordinate));
                    const distanceFromRoute = distanceToRouteMeters(route, rawCoordinate);
                    const thresholdMeters = gpsAccuracyThresholdMeters(position.coords.accuracy);
                    if (distanceFromRoute > thresholdMeters) {
                        const previous = offRouteStateRef.current;
                        offRouteStateRef.current = {
                            count: previous.count + 1,
                            firstAt: previous.firstAt || timestamp,
                            maxDistance: Math.max(previous.maxDistance || 0, distanceFromRoute),
                        };
                    } else {
                        offRouteStateRef.current = { count: 0, firstAt: null, maxDistance: 0 };
                    }

                    const offRouteState = offRouteStateRef.current;
                    const elapsedMs = offRouteState.firstAt ? timestamp - offRouteState.firstAt : 0;
                    // Selvästi reitiltä (esim. risteyksestä ohi ajettu): reititä nopeammin.
                    const hardOffRoute = offRouteState.maxDistance >= thresholdMeters * OFF_ROUTE_HARD_MULTIPLIER
                        && offRouteState.count >= OFF_ROUTE_HARD_CONFIRMATION_COUNT
                        && elapsedMs >= OFF_ROUTE_HARD_CONFIRMATION_MS;
                    const normalOffRoute = offRouteState.count >= OFF_ROUTE_CONFIRMATION_COUNT
                        && elapsedMs >= OFF_ROUTE_CONFIRMATION_MS;
                    if (!reroutingRef.current && (hardOffRoute || normalOffRoute)) {
                        offRouteStateRef.current = { count: 0, firstAt: null, maxDistance: 0 };
                        rerouteFromCurrentRef.current?.(nextCoordinate);
                    }
                }
                setNavigationMode(route && mode !== 'tracking' ? 'gps' : 'tracking');
            },
            (positionError) => {
                // Navigoinnin aikana lyhyt signaalikato EI keskeytä: dead reckoning jatkaa
                // reittiä pitkin. Näytetään kova virhe vain jos lupa on evätty (code 1);
                // muuten rauhallinen viesti eikä navigointia lopeteta.
                if (positionError.code === 1 || !selectedRouteRef.current) {
                    setGpsError(geolocationErrorMessage(positionError));
                } else {
                    setGpsError('Heikko GPS-signaali – jatketaan reittiä arvioidulla vauhdilla.');
                }
            },
            {
                enableHighAccuracy: true,
                maximumAge: 5000,
                timeout: 30000,
            }
        );
    }, []);

    // Säilytä navigointitila reloadin yli.
    useEffect(() => {
        try {
            window.localStorage.setItem(NAV_MODE_STORAGE_KEY, navigationMode);
        } catch (storageError) {
            // localStorage ei käytettävissä – ohitetaan.
        }
    }, [navigationMode]);

    // Pidä ruutu hereillä navigoinnin aikana (estä ruudunsäästäjä / näytön lukitus).
    useEffect(() => {
        const navigatingNow = navigationMode === 'gps' || navigationMode === 'running';
        if (!navigatingNow || !('wakeLock' in navigator)) {
            return undefined;
        }

        let released = false;

        const requestWakeLock = async () => {
            try {
                wakeLockRef.current = await navigator.wakeLock.request('screen');
            } catch (wakeError) {
                // Wake Lock voi epäonnistua (esim. akkusäästö) – navigointi jatkuu silti.
            }
        };

        // Selain vapauttaa lukon kun välilehti piiloutuu; pyydä uudelleen kun palataan.
        const handleVisibility = () => {
            if (!released && document.visibilityState === 'visible') {
                requestWakeLock();
            }
        };

        requestWakeLock();
        document.addEventListener('visibilitychange', handleVisibility);

        return () => {
            released = true;
            document.removeEventListener('visibilitychange', handleVisibility);
            if (wakeLockRef.current) {
                wakeLockRef.current.release().catch(() => {});
                wakeLockRef.current = null;
            }
        };
    }, [navigationMode]);

    // Palauta GPS-navigointi reloadin jälkeen, kun reitti on palautunut.
    useEffect(() => {
        if (navigationMode === 'gps' && selectedRoute?.coordinates?.length && geolocationWatchRef.current === null) {
            startGpsWatch();
        }
    }, [navigationMode, selectedRoute, startGpsWatch]);

    useEffect(() => {
        if ('speechSynthesis' in window && typeof window.speechSynthesis.getVoices === 'function') {
            const updateVoices = () => {
                setNavigationVoices(sortedNavigationVoices(window.speechSynthesis.getVoices()));
            };
            updateVoices();
            window.speechSynthesis.onvoiceschanged = updateVoices;
            return () => {
                if (window.speechSynthesis.onvoiceschanged === updateVoices) {
                    window.speechSynthesis.onvoiceschanged = null;
                }
            };
        }
        return undefined;
    }, []);

    useEffect(() => {
        const fetchRadarLayer = async () => {
            if (!auth?.token) {
                setRadarStatus('idle');
                return;
            }

            setRadarStatus('loading');
            try {
                const response = await axios.get('https://api.rainviewer.com/public/weather-maps.json');
                const latestFrame = response.data?.radar?.past?.at(-1);
                if (!response.data?.host || !latestFrame?.path) {
                    throw new Error('Sadetutkan tietoja ei löytynyt.');
                }

                radarLayerRef.current?.setSource(new XYZ({
                    url: `${response.data.host}${latestFrame.path}/256/{z}/{x}/{y}/2/1_1.png`,
                    attributions: 'RainViewer',
                    crossOrigin: 'anonymous',
                    maxZoom: RAIN_RADAR_MAX_ZOOM,
                    minZoom: MAP_MIN_ZOOM,
                    zDirection: -1,
                    tileLoadFunction: (tile, src) => {
                        const zoom = tile.getTileCoord()?.[0] ?? 0;
                        tile.getImage().src = zoom > RAIN_RADAR_MAX_ZOOM ? TRANSPARENT_TILE : src;
                    },
                }));
                setRadarStatus('ready');
            } catch (radarError) {
                setRadarStatus('error');
            }
        };

        fetchRadarLayer();
    }, [auth?.token]);

    useEffect(() => {
        if (!auth?.token) {
            return;
        }

        const token = auth.token;

        const fetchCurrentUser = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/auth/me`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                const nextAuth = { token, user: response.data };
                setAuth(nextAuth);
                window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextAuth));
            } catch (authCheckError) {
                setAuth(null);
                window.localStorage.removeItem(AUTH_STORAGE_KEY);
            }
        };

        fetchCurrentUser();
    }, [auth?.token]);

    useEffect(() => {
        const fetchSpeedCameras = async () => {
            const speedCameraSource = speedCameraLayerRef.current?.getSource();
            if (!speedCameraSource) {
                return;
            }

            speedCameraSource.clear();
            setSpeedCameraCount(0);

            if (!speedCamerasEnabled || !selectedRoute?.coordinates?.length) {
                setSpeedCameraStatus('idle');
                return;
            }

            const cacheKey = speedCameraCacheKey(selectedRoute);
            if (!cacheKey) {
                setSpeedCameraStatus('idle');
                return;
            }

            setSpeedCameraStatus('loading');
            try {
                const cameraResponse = speedCameraCacheRef.current.has(cacheKey)
                    ? speedCameraCacheRef.current.get(cacheKey)
                    : (await axios.post(`${API_BASE_URL}/api/speed-cameras`, {
                        coordinates: selectedRoute.coordinates,
                    })).data;
                if (!speedCameraCacheRef.current.has(cacheKey)) {
                    speedCameraCacheRef.current.set(cacheKey, cameraResponse);
                }

                const cameras = cameraResponse?.cameras || [];
                const features = cameras
                    .filter((camera) => Number.isFinite(camera.lat) && Number.isFinite(camera.lon))
                    .map((camera) => new Feature({
                        geometry: new Point(fromLonLat([camera.lon, camera.lat])),
                        maxspeed: camera.maxspeed || '',
                        label: camera.maxspeed ? `Nopeuskamera · ${camera.maxspeed} km/h` : 'Nopeuskamera',
                    }));

                speedCameraSource.addFeatures(features);
                setSpeedCameraCount(features.length);
                setSpeedCameraSourceInfo(cameraResponse?.dataSource
                    ? {
                        dataSource: cameraResponse.dataSource,
                        sourceUrl: cameraResponse.sourceUrl,
                        license: cameraResponse.license,
                    }
                    : SPEED_CAMERA_SOURCE_INFO);
                setSpeedCameraStatus('ready');
            } catch (speedCameraError) {
                setSpeedCameraCount(0);
                setSpeedCameraSourceInfo(SPEED_CAMERA_SOURCE_INFO);
                setSpeedCameraStatus('ready');
            }
        };

        fetchSpeedCameras();
    }, [selectedRoute, speedCamerasEnabled]);

    useEffect(() => {
        const fetchRoadContext = async () => {
            const roadContextSource = roadContextLayerRef.current?.getSource();
            if (!roadContextSource) {
                return;
            }

            roadContextSource.clear();
            setRoadContextFeatures([]);

            if (!roadContextEnabled || !selectedRoute?.coordinates?.length) {
                setRoadContextStatus('idle');
                return;
            }

            const cacheKey = roadContextCacheKey(selectedRoute);
            if (!cacheKey) {
                setRoadContextStatus('idle');
                return;
            }

            setRoadContextStatus('loading');
            try {
                const contextResponse = roadContextCacheRef.current.has(cacheKey)
                    ? roadContextCacheRef.current.get(cacheKey)
                    : (await axios.post(`${API_BASE_URL}/api/road-context`, {
                        coordinates: selectedRoute.coordinates,
                    })).data;
                if (!roadContextCacheRef.current.has(cacheKey)) {
                    roadContextCacheRef.current.set(cacheKey, contextResponse);
                }

                const contextFeatures = contextResponse?.features || [];
                const mapFeatures = contextFeatures
                    .filter((feature) => Number.isFinite(feature.lat) && Number.isFinite(feature.lon))
                    .map((feature) => new Feature({
                        geometry: new Point(fromLonLat([feature.lon, feature.lat])),
                        type: feature.type,
                        value: feature.value || '',
                        label: roadContextLabel(feature),
                    }));

                roadContextSource.addFeatures(mapFeatures);
                setRoadContextFeatures(contextFeatures);
                setRoadContextSourceInfo(contextResponse?.dataSource
                    ? {
                        dataSource: contextResponse.dataSource,
                        sourceUrl: contextResponse.sourceUrl,
                        license: contextResponse.license,
                    }
                    : ROAD_CONTEXT_SOURCE_INFO);
                setRoadContextStatus('ready');
            } catch (roadContextError) {
                setRoadContextFeatures([]);
                setRoadContextSourceInfo(ROAD_CONTEXT_SOURCE_INFO);
                setRoadContextStatus('error');
            }
        };

        fetchRoadContext();
    }, [roadContextEnabled, selectedRoute]);

    useEffect(() => {
        const fetchDigitraffic = async () => {
            const source = digitrafficLayerRef.current?.getSource();
            if (!source) {
                return;
            }
            source.clear();
            setDigitrafficItems([]);

            if (!digitrafficEnabled || !selectedRoute?.coordinates?.length) {
                setDigitrafficStatus('idle');
                return;
            }

            setDigitrafficStatus('loading');
            try {
                const response = (await axios.post(`${API_BASE_URL}/api/digitraffic/road-traffic`, {
                    coordinates: selectedRoute.coordinates,
                })).data;

                const items = response?.items || [];
                const mapFeatures = items
                    .filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lon))
                    .map((item) => new Feature({
                        geometry: new Point(fromLonLat([item.lon, item.lat])),
                        type: item.type,
                        label: [item.title, item.locationDescription, item.details].filter(Boolean).join('\n'),
                    }));

                source.addFeatures(mapFeatures);
                setDigitrafficItems(items);
                setDigitrafficStatus('ready');
            } catch (digitrafficError) {
                setDigitrafficItems([]);
                setDigitrafficStatus('error');
            }
        };

        fetchDigitraffic();
    }, [digitrafficEnabled, selectedRoute]);

    useEffect(() => {
        const fetchFluency = async () => {
            const markerSource = fluencyLayerRef.current?.getSource();
            const congestionSource = routeCongestionLayerRef.current?.getSource();
            markerSource?.clear();
            congestionSource?.clear();
            setFluencyItems([]);

            if (!selectedRoute?.coordinates?.length) {
                setFluencyStatus('idle');
                return;
            }

            setFluencyStatus('loading');
            try {
                const response = (await axios.post(`${API_BASE_URL}/api/digitraffic/road-fluency`, {
                    coordinates: selectedRoute.coordinates,
                })).data;

                const items = response?.items || [];

                // Sujuvuusmerkit (näkyvyys togglataan erikseen fluencyEnabledilla).
                const markerFeatures = items
                    .filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lon))
                    .map((item) => {
                        const speed = Number.isFinite(item.speedKmh) ? item.speedKmh : null;
                        const percent = Number.isFinite(item.freeFlowPercent) ? item.freeFlowPercent : null;
                        const label = `Sujuvuus${speed != null ? `: ${Math.round(speed)} km/h` : ''}${percent != null ? ` (${Math.round(percent)} % vapaasta)` : ''}`;
                        return new Feature({
                            geometry: new Point(fromLonLat([item.lon, item.lat])),
                            speed,
                            percent,
                            label,
                        });
                    });
                markerSource?.addFeatures(markerFeatures);

                // Reittiviivan värjäys ruuhkan mukaan (aina päällä, riippumaton merkeistä).
                const congested = items.filter((item) => Number.isFinite(item.freeFlowPercent) && item.freeFlowPercent < 85);
                if (congested.length && congestionSource) {
                    const coords = selectedRoute.coordinates;
                    const bandAt = (lon, lat) => {
                        let worst = null;
                        for (const c of congested) {
                            if (coordinateDistanceMeters([lon, lat], [c.lon, c.lat]) < 1500) {
                                worst = worst == null ? c.freeFlowPercent : Math.min(worst, c.freeFlowPercent);
                            }
                        }
                        if (worst == null) {
                            return null;
                        }
                        return worst < 60 ? 'red' : 'orange';
                    };
                    const segmentFeatures = [];
                    let segmentStart = 0;
                    let currentBand = bandAt(coords[0].lon, coords[0].lat);
                    for (let i = 1; i < coords.length; i++) {
                        const band = bandAt(coords[i].lon, coords[i].lat);
                        if (band !== currentBand) {
                            if (currentBand) {
                                const line = coords.slice(segmentStart, i + 1).map((c) => fromLonLat([c.lon, c.lat]));
                                if (line.length > 1) {
                                    segmentFeatures.push(new Feature({ geometry: new LineString(line), band: currentBand }));
                                }
                            }
                            segmentStart = i;
                            currentBand = band;
                        }
                    }
                    if (currentBand) {
                        const line = coords.slice(segmentStart).map((c) => fromLonLat([c.lon, c.lat]));
                        if (line.length > 1) {
                            segmentFeatures.push(new Feature({ geometry: new LineString(line), band: currentBand }));
                        }
                    }
                    congestionSource.addFeatures(segmentFeatures);
                }

                setFluencyItems(items);
                setFluencyStatus('ready');
            } catch (fluencyError) {
                setFluencyItems([]);
                setFluencyStatus('error');
            }
        };

        fetchFluency();
    }, [selectedRoute, congestionTick]);

    // Navigointitilan vaihtuessa piirrä reitti uudelleen (vaalean vihreä ajossa).
    useEffect(() => {
        navigationModeRef.current = navigationMode;
        routeLayerRef.current?.changed();
    }, [navigationMode]);

    useEffect(() => {
        try {
            window.localStorage.setItem(VEHICLE_STORAGE_KEY, JSON.stringify(vehicle));
        } catch (storageError) {
            // ohitetaan
        }
    }, [vehicle]);

    // Auto-päivitä ruuhka-/sujuvuustiedot navigoinnin aikana ~5 min välein.
    useEffect(() => {
        if (navigationMode !== 'gps' && navigationMode !== 'running') {
            return undefined;
        }
        const intervalId = setInterval(() => setCongestionTick((tick) => tick + 1), 5 * 60 * 1000);
        return () => clearInterval(intervalId);
    }, [navigationMode]);

    useEffect(() => {
        const fetchCharging = async () => {
            const source = chargingLayerRef.current?.getSource();
            if (!source) {
                return;
            }
            source.clear();
            setChargingItems([]);

            if (!chargingEnabled || !selectedRoute?.coordinates?.length) {
                setChargingStatus('idle');
                return;
            }

            setChargingStatus('loading');
            try {
                const response = (await axios.post(`${API_BASE_URL}/api/digitraffic/charging`, {
                    coordinates: selectedRoute.coordinates,
                })).data;

                const items = response?.items || [];
                const mapFeatures = items
                    .filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lon))
                    .map((item) => new Feature({
                        geometry: new Point(fromLonLat([item.lon, item.lat])),
                        fast: item.fastCharge,
                        power: item.maxPowerKw,
                        label: [
                            item.name || 'Latauspiste',
                            item.operator,
                            `${item.maxPowerKw} kW · ${item.connectorCount} liitintä${item.fastCharge ? ' · pikalataus (DC)' : ''}`,
                        ].filter(Boolean).join('\n'),
                    }));

                source.addFeatures(mapFeatures);
                setChargingItems(items);
                setChargingStatus('ready');
            } catch (chargingError) {
                setChargingItems([]);
                setChargingStatus('error');
            }
        };

        fetchCharging();
    }, [chargingEnabled, selectedRoute]);

    useEffect(() => {
        const assessRoutes = async () => {
            if (!tripPlan?.routes?.length) {
                setTrafficAdvice(null);
                return;
            }
            try {
                const routes = tripPlan.routes
                    .filter((route) => route.coordinates?.length)
                    .map((route) => ({
                        index: route.index,
                        durationSeconds: route.durationSeconds,
                        coordinates: route.coordinates,
                    }));
                if (!routes.length) {
                    setTrafficAdvice(null);
                    return;
                }
                const response = (await axios.post(`${API_BASE_URL}/api/digitraffic/route-assessment`, { routes })).data;
                setTrafficAdvice(response);
            } catch (assessError) {
                setTrafficAdvice(null);
            }
        };

        assessRoutes();
    }, [tripPlan]);

    // Ei automaattista oletusreitin laskentaa latauksessa: käyttäjä valitsee
    // alkuruudussa (anonyymi / kirjautuminen) ja laskee reitin itse.
    // Palauta kuitenkin viimeisin reitti paluulla: kirjautuneena backendista
    // (/api/trip/last), muuten paikallisesta localStoragesta.
    useEffect(() => {
        if (lastTripRestoredRef.current) {
            return;
        }
        lastTripRestoredRef.current = true;

        if (auth?.token) {
            axios.get(`${API_BASE_URL}/api/trip/last`, {
                headers: { Authorization: `Bearer ${auth.token}` },
            })
                .then((response) => {
                    const plan = response.data;
                    applyStoredTrip({
                        plan,
                        start: plan.origin,
                        end: plan.destination,
                        mode: plan.travelMode,
                        preference: plan.routePreference,
                    });
                })
                .catch(() => {
                    const local = loadStoredLastTrip();
                    if (local) {
                        applyStoredTrip(local);
                    }
                });
        } else {
            const local = loadStoredLastTrip();
            if (local) {
                applyStoredTrip(local);
            }
        }
    }, [auth, applyStoredTrip]);

    useEffect(() => {
        // Reitin palautus (reload) ei saa nollata navigointia – ohitetaan kerran.
        if (restoringTripRef.current) {
            restoringTripRef.current = false;
            return;
        }
        setNavigationMode('idle');
        navManualViewUntilRef.current = 0;
        if (navRestoreTimerRef.current) {
            window.clearTimeout(navRestoreTimerRef.current);
            navRestoreTimerRef.current = null;
        }
        setNavigationProgress(0);
        setUserCoordinate(null);
        setCurrentSpeedKmh(null);
        setGpsError(null);
        lastSpokenInstructionRef.current = null;
        maxReachedInstructionIndexRef.current = -1;
        lastPreparedInstructionRef.current = null;
        lastExecuteInstructionRef.current = null;
        lastGpsSampleRef.current = null;
        offRouteStateRef.current = { count: 0, firstAt: null, maxDistance: 0 };
        if (geolocationWatchRef.current !== null) {
            navigator.geolocation?.clearWatch(geolocationWatchRef.current);
            geolocationWatchRef.current = null;
        }
    }, [selectedRouteIndex, tripPlan]);

    useEffect(() => () => {
        if (geolocationWatchRef.current !== null) {
            navigator.geolocation?.clearWatch(geolocationWatchRef.current);
            geolocationWatchRef.current = null;
        }
    }, []);

    useEffect(() => {
        if ((navigationMode !== 'running' && navigationMode !== 'gps') || !currentInstruction) {
            return;
        }

        // 1) Valmistautuminen: kun tulevaan käännökseen on n. 300 m, kerro se etukäteen.
        if (currentInstruction.nextText
            && currentInstruction.remainingToNext <= 300
            && currentInstruction.remainingToNext > 40
            && lastPreparedInstructionRef.current !== currentInstruction.index) {
            lastPreparedInstructionRef.current = currentInstruction.index;
            speakInstruction(
                { text: `Valmistaudu: ${currentInstruction.nextText}, ${instructionDistanceLabel(currentInstruction.remainingToNext)} päästä` },
                navigationVoiceKey
            );
            return;
        }

        // 2) Toteutus: juuri ennen käännöstä (n. 30 m) kerro itse käännös,
        // jotta ilmoitus ehtii kuulua täsmälleen oikeassa kohdassa.
        if (currentInstruction.nextText
            && currentInstruction.remainingToNext <= 30
            && lastExecuteInstructionRef.current !== currentInstruction.index) {
            lastExecuteInstructionRef.current = currentInstruction.index;
            speakInstruction({ text: currentInstruction.nextText }, navigationVoiceKey);
            return;
        }

        // Älä kuuluta uudelleen tietä, joka on jo ohitettu. GPS-heilahdus voi hetkeksi
        // siirtää edistymän taaksepäin – silloin ei toisteta "jatka tietä 101".
        if (currentInstruction.index <= maxReachedInstructionIndexRef.current
            || lastSpokenInstructionRef.current === currentInstruction.index) {
            return;
        }

        // Peruspuhe segmenttiin siirryttäessä: vahvista NYKYINEN tie ("Jatka tietä 101"),
        // EI tulevaa käännöstä (sen hoitavat valmistautuminen 300 m ja toteutus 30 m).
        maxReachedInstructionIndexRef.current = currentInstruction.index;
        lastSpokenInstructionRef.current = currentInstruction.index;
        speakInstruction({ text: currentInstruction.currentRoadText }, navigationVoiceKey);
    }, [currentInstruction, navigationMode, navigationVoiceKey]);

    useEffect(() => {
        try {
            window.localStorage.setItem(NAVIGATION_VOICE_STORAGE_KEY, navigationVoiceKey);
        } catch (storageError) {
            // Local storage is optional for the voice preference.
        }
    }, [navigationVoiceKey]);

    useEffect(() => {
        if (navigationTimerRef.current) {
            clearInterval(navigationTimerRef.current);
            navigationTimerRef.current = null;
        }

        if (navigationMode !== 'running' || !selectedRoute?.durationSeconds) {
            return undefined;
        }

        navigationTimerRef.current = setInterval(() => {
            setNavigationProgress((currentProgress) => {
                const nextProgress = currentProgress + (60 / selectedRoute.durationSeconds);
                if (nextProgress >= 1) {
                    clearInterval(navigationTimerRef.current);
                    navigationTimerRef.current = null;
                    setNavigationMode('ready');
                    return 1;
                }
                return nextProgress;
            });
        }, 1000);

        return () => {
            if (navigationTimerRef.current) {
                clearInterval(navigationTimerRef.current);
                navigationTimerRef.current = null;
            }
        };
    }, [navigationMode, selectedRoute]);

    // Dead reckoning GPS-katkoksen aikana: jos viimeisimmästä kelvollisesta GPS-fixistä
    // on kulunut liian kauan (tunneli, katve), ei jäädä paikalleen odottamaan eikä
    // hypitä – jatketaan reittiä pitkin viimeksi mitatulla nopeudella (tai rajoituksella,
    // viime kädessä 80 km/h). Näin kartta liikkuu tasaisesti ja oletuksena matka jatkuu
    // samaa tietä, kunnes GPS palaa. Ei arvailla loputtomiin (DEAD_RECKON_MAX_MS).
    useEffect(() => {
        if (navigationMode !== 'gps') {
            return undefined;
        }
        const timer = setInterval(() => {
            const route = selectedRouteRef.current;
            if (!route?.coordinates?.length || !route.distanceMeters) {
                return;
            }
            const lastSample = lastGpsSampleRef.current;
            const now = Date.now();
            // Tuore GPS-fix ajaa itse – ei arvailla. Ei myöskään arvailla ilman lähtökohtaa
            // eikä liian pitkää katvetta (silloin näytetään mieluummin virhe/odotetaan).
            if (!lastSample) {
                return;
            }
            const sinceFixMs = now - lastSample.timestamp;
            if (sinceFixMs < DEAD_RECKON_START_MS || sinceFixMs > DEAD_RECKON_MAX_MS) {
                return;
            }
            // Etene vain jos on todiste liikkeestä: viimeksi mitattu nopeus. Jos GPS
            // näyttää paikallaanoloa (~0) tai nopeutta ei ole mitattu, ei arvata matkaa
            // eteenpäin – muuten "jatka tietä" jatkaisi vaikka seisoisit paikallasi.
            const measuredSpeedKmh = currentSpeedRef.current;
            if (!Number.isFinite(measuredSpeedKmh) || measuredSpeedKmh < DEAD_RECKON_MIN_MOVING_KMH) {
                return;
            }
            const deltaMeters = (measuredSpeedKmh / 3.6) * (DEAD_RECKON_TICK_MS / 1000);
            const nextProgress = Math.min(1, navigationProgressRef.current + deltaMeters / route.distanceMeters);
            const mapCoordinate = navigationCoordinate(route, nextProgress, null);
            if (mapCoordinate) {
                const [lon, lat] = toLonLat(mapCoordinate);
                // Vie auto reitin viivalle (talon numeron tarkkuudella tie jatkuu) – ei raakaa GPS-pistettä.
                lastGpsSampleRef.current = { ...lastSample, coordinate: [lon, lat] };
                setUserCoordinate([lon, lat]);
            }
            setNavigationProgress(nextProgress);
            setGpsError('Heikko GPS-signaali – jatketaan reittiä arvioidulla vauhdilla.');
        }, DEAD_RECKON_TICK_MS);
        return () => clearInterval(timer);
    }, [navigationMode]);

    const [locating, setLocating] = useState(false);

    // Hakee nykyisen sijainnin "lat,lon"-muodossa (null jos ei saatu).
    const resolveCurrentLocation = () => new Promise((resolve) => {
        if (!navigator.geolocation) {
            setError('Sijaintipalvelu ei ole käytettävissä tällä laitteella.');
            resolve(null);
            return;
        }
        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setLocating(false);
                resolve(`${position.coords.latitude.toFixed(5)},${position.coords.longitude.toFixed(5)}`);
            },
            (positionError) => {
                setLocating(false);
                setError(geolocationErrorMessage(positionError));
                resolve(null);
            },
            { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
        );
    });

    const askAuthOrAnonymous = (plan) => {
        // Sovellus vaatii kirjautumisen (kartta on gaten takana), joten tässä ollaan
        // aina kirjautuneina. Lasketaan reitti suoraan.
        fetchTripPlan(plan.startValue, plan.endValue, plan.departureValue, plan.modeValue, plan.preferenceValue);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (startMode === 'location') {
            if (!end.trim()) {
                setError('Anna kohde.');
                return;
            }
            const coordinate = await resolveCurrentLocation();
            if (!coordinate) {
                return;
            }
            setStart(coordinate);
            askAuthOrAnonymous({
                startValue: coordinate,
                endValue: end,
                departureValue: departure,
                modeValue: travelMode,
                preferenceValue: routePreference,
            });
            return;
        }
        askAuthOrAnonymous({
            startValue: start,
            endValue: end,
            departureValue: departure,
            modeValue: travelMode,
            preferenceValue: routePreference,
        });
    };

    const handleOpenRouteSearch = () => {
        setRouteSearchOpen(true);
    };

    const handleClosePopup = () => {
        setFeatureInfo(null);
        popupOverlayRef.current?.setPosition(undefined);
    };

    const closePick = () => {
        setPickPoint(null);
        setPickAddress(null);
        pickOverlayRef.current?.setPosition(undefined);
    };

    // Aseta pudotettu nasta lähdöksi tai kohteeksi ja laske reitti.
    // Reititykseen käytetään tarkkaa koordinaattia; näytössä käytetään osoitenimeä.
    const handleSetPickAs = async (field) => {
        if (!pickPoint) {
            return;
        }
        const coordValue = `${pickPoint.lat.toFixed(5)},${pickPoint.lon.toFixed(5)}`;
        const displayValue = pickAddress?.label && !pickAddress.label.includes(',')
            ? pickAddress.label
            : coordValue;

        if (field === 'start') {
            setStart(displayValue);
            setStartMode('custom');
            closePick();
            fetchTripPlan(coordValue, end, departure, travelMode, routePreference);
            return;
        }

        // Kohde: lähtö nykyisestä sijainnista jos startMode='location', muuten nykyinen lähtö.
        setEnd(displayValue);
        closePick();
        if (startMode === 'location') {
            const startCoord = await resolveCurrentLocation();
            if (!startCoord) {
                return;
            }
            setStart(startCoord);
            fetchTripPlan(startCoord, coordValue, departure, travelMode, routePreference);
        } else {
            fetchTripPlan(start, coordValue, departure, travelMode, routePreference);
        }
    };

    const handleEditEndpoint = (field) => {
        if (field === 'start') {
            setEditValue(startMode === 'location' ? '' : start);
        } else {
            setEditValue(end);
        }
        setEditingField(field);
    };

    const handleUseLocationForEndpoint = async () => {
        const coordinate = await resolveCurrentLocation();
        if (!coordinate) {
            return;
        }
        setStart(coordinate);
        setStartMode('location');
        setEditingField(null);
        fetchTripPlan(coordinate, end, departure, travelMode, routePreference);
    };

    const handleSaveEndpoint = (event) => {
        event.preventDefault();
        const value = editValue.trim();
        if (!value) {
            return;
        }
        if (editingField === 'start') {
            setStart(value);
            setStartMode('custom');
            fetchTripPlan(value, end, departure, travelMode, routePreference);
        } else {
            setEnd(value);
            fetchTripPlan(start, value, departure, travelMode, routePreference);
        }
        setEditingField(null);
    };

    const handleOpenSettings = () => {
        setHomeAddress(auth?.user?.homeAddress || '');
        setWorkAddress(auth?.user?.workAddress || '');
        setAuthError(null);
        setSettingsOpen(true);
    };

    const handleSaveAddresses = async () => {
        if (!auth?.token) {
            return;
        }
        try {
            const response = (await axios.put(`${API_BASE_URL}/api/auth/addresses`,
                { homeAddress, workAddress },
                { headers: { Authorization: `Bearer ${auth.token}` } })).data;
            const nextAuth = { ...auth, user: response };
            setAuth(nextAuth);
            window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextAuth));
            setSettingsOpen(false);
        } catch (saveError) {
            setAuthError(saveError.response?.data?.message || saveError.message);
        }
    };

    // Onboardingin viimeistely: tallenna oletusliikkumistapa ja (jos annettu) kotiosoite.
    const handleFinishOnboarding = async () => {
        try {
            window.localStorage.setItem(DEFAULT_TRAVEL_MODE_STORAGE_KEY, travelMode);
        } catch (storageError) {
            // Oletusliikkumistavan tallennus on paras yritys.
        }
        if (auth?.token && homeAddress.trim() && homeAddress.trim() !== (auth.user?.homeAddress || '')) {
            try {
                const response = (await axios.put(`${API_BASE_URL}/api/auth/addresses`,
                    { homeAddress: homeAddress.trim(), workAddress },
                    { headers: { Authorization: `Bearer ${auth.token}` } })).data;
                const nextAuth = { ...auth, user: response };
                setAuth(nextAuth);
                window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextAuth));
            } catch (saveError) {
                // Ei estetä aloitusta jos osoitteen tallennus epäonnistuu; sen voi tehdä asetuksista.
            }
        }
        setOnboardingOpen(false);
        setRouteSearchOpen(true);
    };

    const handleTravelModeChange = (nextTravelMode) => {
        const nextPreference = nextTravelMode === 'foot' ? 'balanced' : routePreference;
        setTravelMode(nextTravelMode);
        try {
            window.localStorage.setItem(DEFAULT_TRAVEL_MODE_STORAGE_KEY, nextTravelMode);
        } catch (storageError) {
            // Oletusliikkumistavan muistaminen on paras yritys.
        }
        setRoutePreference(nextPreference);
        // Päivitä reitti vain jos lähtö ja kohde on jo annettu.
        if (start.trim() && end.trim()) {
            fetchTripPlan(start, end, departure, nextTravelMode, nextPreference);
        }
    };

    const handleRoutePreferenceChange = (nextPreference) => {
        setRoutePreference(nextPreference);
        if (start.trim() && end.trim()) {
            fetchTripPlan(start, end, departure, travelMode, nextPreference);
        }
    };

    const handleAuthSubmit = async (event) => {
        event.preventDefault();
        const registering = authMode === 'register';
        setAuthLoading(true);
        setAuthError(null);

        try {
            const response = await axios.post(`${API_BASE_URL}/api/auth/${authMode}`, {
                username: authUsername,
                password: authPassword,
            });
            const nextAuth = response.data;
            setAuth(nextAuth);
            window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(nextAuth));
            setAuthPassword('');
            setHomeAddress(nextAuth.user?.homeAddress || '');
            setWorkAddress(nextAuth.user?.workAddress || '');
            setSettingsOpen(false);
            initialLoadDoneRef.current = false;
            // Ensikäynti (rekisteröinti) tai kotiosoite puuttuu → näytä onboarding
            // (oletusliikkumistapa + kotiosoite). Muuten mennään suoraan karttaan.
            setOnboardingOpen(registering || !nextAuth.user?.homeAddress);
            const plan = pendingAnonymousPlan;
            setPendingAnonymousPlan(null);
            fetchTripPlan(
                plan?.startValue || start,
                plan?.endValue || end,
                plan?.departureValue || departure,
                plan?.modeValue || travelMode,
                plan?.preferenceValue || routePreference,
                nextAuth
            );
        } catch (requestError) {
            setAuthError(requestError.response?.data?.message || requestError.message);
        } finally {
            setAuthLoading(false);
        }
    };

    const handleLogout = () => {
        setAuth(null);
        window.localStorage.removeItem(AUTH_STORAGE_KEY);
        setTripPlan(null);
        setRouteSearchOpen(true);
        setNavigationMode('idle');
        setNavigationProgress(0);
        setCurrentSpeedKmh(null);
        setRadarStatus('idle');
        initialLoadDoneRef.current = false;
    };

    const handlePrepareNavigation = () => {
        if (!selectedRoute) {
            return;
        }
        if (!navigator.geolocation) {
            setGpsError('GPS-paikannus ei ole käytettävissä tässä selaimessa.');
            return;
        }

        setGpsError(null);
        lastSpokenInstructionRef.current = null;
        maxReachedInstructionIndexRef.current = -1;
        lastPreparedInstructionRef.current = null;
        lastExecuteInstructionRef.current = null;
        lastGpsSampleRef.current = null;
        offRouteStateRef.current = { count: 0, firstAt: null, maxDistance: 0 };
        setCurrentSpeedKmh(null);
        navZoomedRef.current = false;
        setNavigationMode('gps');
        if (navigationTimerRef.current) {
            clearInterval(navigationTimerRef.current);
            navigationTimerRef.current = null;
        }
        startGpsWatch();
    };

    const handleStartLocationTracking = () => {
        if (!navigator.geolocation) {
            setGpsError('GPS-paikannus ei ole käytettävissä tässä selaimessa.');
            return;
        }
        setGpsError(null);
        setRouteSearchOpen(false);
        setNavigationProgress(0);
        setCurrentSpeedKmh(null);
        lastGpsSampleRef.current = null;
        offRouteStateRef.current = { count: 0, firstAt: null, maxDistance: 0 };
        navZoomedRef.current = false;
        if (navigationTimerRef.current) {
            clearInterval(navigationTimerRef.current);
            navigationTimerRef.current = null;
        }
        setNavigationMode('tracking');
        startGpsWatch('tracking');
    };

    const handleStartNavigation = () => {
        if (!selectedRoute) {
            return;
        }
        if (geolocationWatchRef.current !== null) {
            navigator.geolocation?.clearWatch(geolocationWatchRef.current);
            geolocationWatchRef.current = null;
        }
        setGpsError(null);
        setUserCoordinate(null);
        setCurrentSpeedKmh(routeSpeedKmh(selectedRoute));
        lastGpsSampleRef.current = null;
        offRouteStateRef.current = { count: 0, firstAt: null, maxDistance: 0 };
        setNavigationProgress(0);
        lastSpokenInstructionRef.current = null;
        maxReachedInstructionIndexRef.current = -1;
        lastPreparedInstructionRef.current = null;
        lastExecuteInstructionRef.current = null;
        navZoomedRef.current = false;
        setNavigationMode('running');
    };

    const handleStopNavigation = () => {
        const wasTrackingOnly = navigationMode === 'tracking' && !selectedRoute;
        if (geolocationWatchRef.current !== null) {
            navigator.geolocation?.clearWatch(geolocationWatchRef.current);
            geolocationWatchRef.current = null;
        }
        setNavigationMode('idle');
        setNavigationProgress(0);
        setUserCoordinate(null);
        setCurrentSpeedKmh(null);
        lastGpsSampleRef.current = null;
        offRouteStateRef.current = { count: 0, firstAt: null, maxDistance: 0 };
        lastSpokenInstructionRef.current = null;
        maxReachedInstructionIndexRef.current = -1;
        lastPreparedInstructionRef.current = null;
        lastExecuteInstructionRef.current = null;
        window.speechSynthesis?.cancel();
        if (wasTrackingOnly) {
            setRouteSearchOpen(true);
        }
    };

    const handleTestNavigationVoice = () => {
        speakInstruction({
            text: 'Käänny 300 metrin päästä oikealle ja jatka suoraan kohti määränpäätä.',
        }, navigationVoiceKey);
    };

    const navigating = navigationMode !== 'idle';

    const consumptionEstimate = (() => {
        if (!selectedRoute || !vehicle?.consumption || !vehicle?.capacity) {
            return null;
        }
        const distanceKm = selectedRoute.distanceMeters / 1000;
        const used = (distanceKm / 100) * vehicle.consumption;
        const usable = vehicle.capacity * ((vehicle.startLevel || 0) / 100);
        const rangeKm = vehicle.consumption > 0 ? (usable / vehicle.consumption) * 100 : 0;
        return {
            distanceKm,
            used,
            rangeKm,
            enough: rangeKm >= distanceKm,
            unit: VEHICLE_UNITS[vehicle.fuelType] || '',
            isEv: vehicle.fuelType === 'ev',
        };
    })();

    // Latauspysähdys-ehdotus: kun sähköauton kantama ei riitä perille, poimi reitin
    // varrelta kauimpana saavutettavissa oleva (mieluiten pika-)laturi ennen kuin akku loppuu.
    const chargingSuggestion = (() => {
        if (!consumptionEstimate || !consumptionEstimate.isEv || consumptionEstimate.enough) {
            return null;
        }
        if (!selectedRoute?.coordinates?.length || !chargingItems.length) {
            return null;
        }
        const { distanceKm, rangeKm } = consumptionEstimate;
        const safeRangeKm = rangeKm * 0.9; // 10 % varareservi

        const candidates = chargingItems
            .filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lon))
            .map((item) => ({
                ...item,
                kmFromStart: routeProgressFromCoordinate(selectedRoute, [item.lon, item.lat]) * distanceKm,
                offRouteM: distanceToRouteMeters(selectedRoute, [item.lon, item.lat]),
            }))
            .filter((item) => item.offRouteM <= 2000 && item.kmFromStart > 5 && item.kmFromStart <= safeRangeKm);

        if (!candidates.length) {
            return null;
        }
        const fast = candidates.filter((item) => item.fastCharge);
        const pool = fast.length ? fast : candidates;
        const pick = pool.sort((first, second) => second.kmFromStart - first.kmFromStart)[0];

        const usedToCharger = (pick.kmFromStart / 100) * vehicle.consumption;
        const startEnergy = vehicle.capacity * ((vehicle.startLevel || 0) / 100);
        const remainingPct = Math.max(0, Math.round(((startEnergy - usedToCharger) / vehicle.capacity) * 100));

        return {
            name: pick.name || 'Latauspiste',
            operator: pick.operator,
            kmFromStart: Math.round(pick.kmFromStart),
            remainingPct,
            powerKw: pick.maxPowerKw,
            fast: pick.fastCharge,
        };
    })();

    // Kompakti säätiivistelmä reittikoosteeseen (mobiilissa näkyvä kortti).
    const routeWeatherSummary = (() => {
        const points = selectedRoute?.weatherPoints;
        if (!points?.length) {
            return null;
        }
        // Näytä sää aina kun pisteissä on oikeaa lämpötiladataa (ei vain weatherEnabled-lipun varassa),
        // jolloin myös palautettu/tallennettu reitti näyttää säätilat.
        const temps = points.map((point) => point.temperatureCelsius).filter(Number.isFinite);
        if (!temps.length) {
            return null;
        }
        const avgTemp = Math.round(temps.reduce((sum, value) => sum + value, 0) / temps.length);
        const maxPrecip = points.reduce((max, point) => Math.max(max, point.precipitationMillimeters || 0), 0);
        return {
            avgTemp,
            maxPrecip,
            wind: selectedRoute.averageHeadwindMetersPerSecond,
        };
    })();

    const advice = trafficAdvice?.recommendation;
    const adviceAssessment = advice && trafficAdvice?.assessments
        ? trafficAdvice.assessments.find((item) => item.index === advice.fastestIndex)
        : null;
    const adviceReason = adviceAssessment
        ? [
            adviceAssessment.blockingCount > 0
                ? 'sulku reitillä'
                : adviceAssessment.closureCount > 0 ? 'sulku/tietyö lähellä' : null,
            adviceAssessment.congestedCount > 0 ? 'ruuhka' : null,
            adviceAssessment.disruptionCount > 0 && adviceAssessment.closureCount === 0 ? 'häiriö' : null,
        ].filter(Boolean).join(' + ')
        : '';
    const adviceHasAlternative = advice && advice.bestIndex !== advice.fastestIndex && advice.savingsSeconds > 60;
    const toMinutes = (seconds) => Math.round(seconds / 60);

    return (
        <main className={`planner-shell ${routeSearchOpen ? 'search-open' : 'search-collapsed'}${navigating ? ' navigating' : ''}`}>
            {!isAuthenticated && (
                <div className="welcome-overlay" role="dialog" aria-modal="true" aria-label="Kirjautuminen">
                    <div className="welcome-card">
                        <h1>Reittisuunnittelu</h1>
                        <p>
                            {authMode === 'register'
                                ? 'Luo käyttäjä aloittaaksesi. Ensimmäisellä kerralla kysymme oletusliikkumistavan ja kotiosoitteen.'
                                : 'Kirjaudu sisään aloittaaksesi reittien suunnittelun.'}
                        </p>
                        <form className="inline-auth-form" onSubmit={handleAuthSubmit}>
                            <div className="auth-tabs" role="tablist" aria-label="Kirjautuminen">
                                <button type="button" className={authMode === 'login' ? 'selected' : ''} onClick={() => { setAuthMode('login'); setAuthError(null); }}>Kirjaudu</button>
                                <button type="button" className={authMode === 'register' ? 'selected' : ''} onClick={() => { setAuthMode('register'); setAuthError(null); }}>Rekisteröidy</button>
                            </div>
                            <input placeholder="Käyttäjätunnus" value={authUsername} onChange={(event) => setAuthUsername(event.target.value)} autoComplete="username" />
                            <input placeholder="Salasana" type="password" value={authPassword} onChange={(event) => setAuthPassword(event.target.value)} autoComplete={authMode === 'register' ? 'new-password' : 'current-password'} />
                            <button type="submit" className="primary" disabled={authLoading}>{authLoading ? 'Odota…' : authMode === 'register' ? 'Luo käyttäjä' : 'Kirjaudu'}</button>
                        </form>
                        {authError && <p className="welcome-footnote welcome-error">{authError}</p>}
                    </div>
                </div>
            )}
            {isAuthenticated && onboardingOpen && (
                <div className="welcome-overlay" role="dialog" aria-modal="true" aria-label="Tervetuloa">
                    <div className="welcome-card">
                        <h1>Tervetuloa, {auth.user.username}!</h1>
                        <p>Asetetaan pari oletusta, niin pääset heti liikkeelle. Voit muuttaa nämä myöhemmin asetuksista.</p>
                        <div className="onboarding-field">
                            <span className="onboarding-label">Oletusliikkumistapa</span>
                            <div className="travel-mode-buttons" role="radiogroup" aria-label="Oletusliikkumistapa">
                                {[['car', 'Auto'], ['bike', 'Pyörä'], ['foot', 'Kävely']].map(([mode, label]) => (
                                    <button
                                        key={mode}
                                        type="button"
                                        role="radio"
                                        aria-checked={travelMode === mode}
                                        className={`travel-mode-button${travelMode === mode ? ' selected' : ''}`}
                                        onClick={() => setTravelMode(mode)}
                                    >
                                        <TravelModeIcon mode={mode} />
                                        <span>{label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <label className="onboarding-field">
                            <span className="onboarding-label">Kotiosoite (valinnainen)</span>
                            <input
                                value={homeAddress}
                                onChange={(event) => setHomeAddress(event.target.value)}
                                placeholder="esim. Mannerheimintie 1, Helsinki"
                            />
                        </label>
                        <div className="welcome-actions">
                            <button type="button" className="primary" onClick={handleFinishOnboarding}>Aloita</button>
                        </div>
                    </div>
                </div>
            )}
            {settingsOpen && (
                <div className="settings-overlay" role="dialog" aria-modal="true" aria-label="Asetukset">
                    <div className="settings-card">
                        <div className="settings-head">
                            <h2>Asetukset</h2>
                            <button type="button" className="settings-close" onClick={() => setSettingsOpen(false)} aria-label="Sulje">×</button>
                        </div>
                        {isAuthenticated ? (
                            <div className="settings-section">
                                <p>Kirjautunut: <strong>{auth.user.username}</strong></p>
                                <label>
                                    Kotiosoite
                                    <input
                                        value={homeAddress}
                                        onChange={(event) => setHomeAddress(event.target.value)}
                                        placeholder="esim. Mannerheimintie 1, Helsinki"
                                    />
                                </label>
                                <label>
                                    Työosoite (valinnainen)
                                    <input
                                        value={workAddress}
                                        onChange={(event) => setWorkAddress(event.target.value)}
                                        placeholder="valinnainen"
                                    />
                                </label>
                                {!auth.user.homeAddress && (
                                    <p className="settings-hint">Lisää kotiosoite, niin saat sen nopeasti lähtö-/kohdepisteeksi.</p>
                                )}
                                <div className="settings-actions">
                                    <button type="button" className="primary" onClick={handleSaveAddresses}>Tallenna</button>
                                    <button type="button" onClick={handleLogout}>Kirjaudu ulos</button>
                                </div>
                            </div>
                        ) : (
                            <div className="settings-section">
                                <form className="inline-auth-form" onSubmit={handleAuthSubmit}>
                                    <div className="auth-tabs" role="tablist" aria-label="Kirjautuminen">
                                        <button type="button" className={authMode === 'login' ? 'selected' : ''} onClick={() => setAuthMode('login')}>Kirjaudu</button>
                                        <button type="button" className={authMode === 'register' ? 'selected' : ''} onClick={() => setAuthMode('register')}>Rekisteröidy</button>
                                    </div>
                                    <input placeholder="Käyttäjätunnus" value={authUsername} onChange={(event) => setAuthUsername(event.target.value)} autoComplete="username" />
                                    <input placeholder="Salasana" type="password" value={authPassword} onChange={(event) => setAuthPassword(event.target.value)} autoComplete={authMode === 'register' ? 'new-password' : 'current-password'} />
                                    <button type="submit" disabled={authLoading}>{authLoading ? 'Odota' : authMode === 'register' ? 'Luo käyttäjä' : 'Kirjaudu'}</button>
                                </form>
                                <p className="anon-note">Säätiedot ja tuulet eivät ole saatavilla anonyymille käyttäjälle. Kirjaudu saadaksesi ne käyttöön.</p>
                            </div>
                        )}
                        <div className="settings-section vehicle-section">
                            <h3>Ajoneuvo</h3>
                            <label>
                                Käyttövoima
                                <select value={vehicle.fuelType} onChange={(event) => setVehicle({ ...vehicle, fuelType: event.target.value })}>
                                    <option value="ev">Sähkö</option>
                                    <option value="petrol">Bensa / diesel</option>
                                    <option value="gas">Kaasu</option>
                                </select>
                            </label>
                            <label>
                                Kulutus ({VEHICLE_UNITS[vehicle.fuelType]}/100 km)
                                <input type="number" min="0" step="0.1" value={vehicle.consumption} onChange={(event) => setVehicle({ ...vehicle, consumption: Number(event.target.value) })} />
                            </label>
                            <label>
                                {vehicle.fuelType === 'ev' ? 'Akku' : 'Tankki'} ({VEHICLE_UNITS[vehicle.fuelType]})
                                <input type="number" min="0" step="0.1" value={vehicle.capacity} onChange={(event) => setVehicle({ ...vehicle, capacity: Number(event.target.value) })} />
                            </label>
                            <label>
                                Lähtötaso (%)
                                <input type="number" min="0" max="100" value={vehicle.startLevel} onChange={(event) => setVehicle({ ...vehicle, startLevel: Number(event.target.value) })} />
                            </label>
                        </div>
                        {authError && <div className="error-message">{authError}</div>}
                        {!isStandalone && (
                            <div className="settings-section install-section">
                                <h3>Sovellus</h3>
                                {installPrompt ? (
                                    <>
                                        <p className="settings-hint">Asenna Reitti kotivalikkoon – aukeaa omana sovelluksenaan ilman selainpalkkia.</p>
                                        <button type="button" className="primary" onClick={handleInstallApp}>
                                            📲 Asenna kotivalikkoon
                                        </button>
                                    </>
                                ) : isIosDevice ? (
                                    <p className="anon-note">
                                        Lisää kotivalikkoon: paina alhaalta <strong>Jaa</strong> (neliö ja nuoli) → <strong>Lisää Koti-valikkoon</strong>.
                                    </p>
                                ) : (
                                    <p className="anon-note">
                                        Voit asentaa sovelluksen kotivalikkoon selaimen valikosta (esim. Chrome: ⋮ → “Asenna sovellus” / “Lisää aloitusnäyttöön”).
                                    </p>
                                )}
                            </div>
                        )}
                        <div className="settings-section settings-about">
                            <button type="button" className="link-button" onClick={() => setHistoryOpen(true)}>
                                Versiohistoria
                            </button>
                            <span className="settings-version">v{APP_VERSION}</span>
                        </div>
                    </div>
                </div>
            )}
            {whatsNewOpen && (
                <div className="settings-overlay" role="dialog" aria-modal="true" aria-label="Mitä uutta" onClick={dismissWhatsNew}>
                    <div className="settings-card whatsnew-card" onClick={(event) => event.stopPropagation()}>
                        <div className="settings-head">
                            <h2>Mitä uutta</h2>
                            <button type="button" className="settings-close" onClick={dismissWhatsNew} aria-label="Sulje">×</button>
                        </div>
                        <div className="settings-section">
                            <p className="whatsnew-lead">{CHANGELOG[0].title} <span className="settings-version">v{CHANGELOG[0].version}</span></p>
                            <ul className="changelog-list">
                                {CHANGELOG[0].changes.map((change) => (
                                    <li key={change}>{change}</li>
                                ))}
                            </ul>
                            <div className="settings-actions">
                                <button type="button" className="primary" onClick={dismissWhatsNew}>Selvä</button>
                                <button type="button" onClick={() => { dismissWhatsNew(); setHistoryOpen(true); }}>Koko historia</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {historyOpen && (
                <div className="settings-overlay" role="dialog" aria-modal="true" aria-label="Versiohistoria" onClick={() => setHistoryOpen(false)}>
                    <div className="settings-card history-card" onClick={(event) => event.stopPropagation()}>
                        <div className="settings-head">
                            <h2>Versiohistoria</h2>
                            <button type="button" className="settings-close" onClick={() => setHistoryOpen(false)} aria-label="Sulje">×</button>
                        </div>
                        <div className="settings-section history-scroll">
                            {CHANGELOG.map((entry) => (
                                <div className="changelog-entry" key={entry.version}>
                                    <div className="changelog-head">
                                        <strong>v{entry.version}</strong>
                                        <span className="changelog-date">{entry.date}</span>
                                    </div>
                                    <p className="changelog-title">{entry.title}</p>
                                    <ul className="changelog-list">
                                        {entry.changes.map((change) => (
                                            <li key={change}>{change}</li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            {iosGuideOpen && (
                <div className="ios-guide-overlay" role="dialog" aria-modal="true" aria-label="Asenna kotivalikkoon" onClick={dismissIosGuide}>
                    <div className="ios-guide-card" onClick={(event) => event.stopPropagation()}>
                        <button type="button" className="settings-close" onClick={dismissIosGuide} aria-label="Sulje">×</button>
                        <h3>Asenna Reitti puhelimeesi</h3>
                        <p>Saat sovelluksen omaksi kuvakkeeksi ja koko ruudulle kahdella napautuksella:</p>
                        <ol className="ios-guide-steps">
                            <li>
                                <span>Paina alapalkin</span>
                                <span className="ios-share-icon" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" width="20" height="20">
                                        <path d="M12 3l4 4M12 3L8 7M12 3v13" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                                        <path d="M5 12v7a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-7" fill="none" stroke="#2563eb" stroke-width="2" stroke-linecap="round"/>
                                    </svg>
                                </span>
                                <span><strong>Jaa</strong>-painiketta</span>
                            </li>
                            <li>Valitse <strong>Lisää Koti-valikkoon</strong></li>
                            <li>Paina <strong>Lisää</strong> oikeasta yläkulmasta</li>
                        </ol>
                        <button type="button" className="primary" onClick={dismissIosGuide}>Selvä</button>
                    </div>
                </div>
            )}
            {editingField && (
                <div className="edit-overlay" role="dialog" aria-modal="true" aria-label="Vaihda paikka" onClick={() => setEditingField(null)}>
                    <div className="edit-card" onClick={(event) => event.stopPropagation()}>
                        <h3>{editingField === 'start' ? 'Lähtöpaikka' : 'Kohde'}</h3>
                        <form onSubmit={handleSaveEndpoint}>
                            <input
                                autoFocus
                                value={editValue}
                                onChange={(event) => setEditValue(event.target.value)}
                                placeholder="Osoite tai paikka"
                            />
                            <div className="edit-quick">
                                {editingField === 'start' && (
                                    <button type="button" onClick={handleUseLocationForEndpoint} disabled={locating}>
                                        {locating ? '…' : '📍 Oma sijainti'}
                                    </button>
                                )}
                                {auth?.user?.homeAddress && (
                                    <button type="button" onClick={() => setEditValue(auth.user.homeAddress)}>🏠 Koti</button>
                                )}
                                {auth?.user?.workAddress && (
                                    <button type="button" onClick={() => setEditValue(auth.user.workAddress)}>💼 Työ</button>
                                )}
                            </div>
                            <div className="edit-actions">
                                <button type="submit" className="primary">Hae reitti</button>
                                <button type="button" onClick={() => setEditingField(null)}>Peruuta</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {advice && advice.level !== 'none' && !navigating && !routeSearchOpen && (
                <div className={`traffic-advice advice-${advice.level}`} role="status">
                    {adviceHasAlternative ? (
                        <>
                            <div className="traffic-advice-line">
                                Nopein normaalisti: reitti {advice.fastestIndex + 1}, {toMinutes(advice.fastestNormalSeconds)} min.
                                {' '}Nyt arvio {toMinutes(advice.fastestAdjustedSeconds)} min{adviceReason ? ` (${adviceReason})` : ''}.
                            </div>
                            <div className="traffic-advice-line traffic-advice-strong">
                                Suositus: reitti {advice.bestIndex + 1}, {toMinutes(advice.bestAdjustedSeconds)} min – säästää ~{toMinutes(advice.savingsSeconds)} min
                            </div>
                            <button type="button" onClick={() => setSelectedRouteIndex(advice.bestIndex)}>
                                Vaihda reitille {advice.bestIndex + 1}
                            </button>
                        </>
                    ) : (
                        <div className="traffic-advice-line">
                            Reitillä hidasta{adviceReason ? ` (${adviceReason})` : ''}: arvio +{toMinutes(advice.fastestAdjustedSeconds - advice.fastestNormalSeconds)} min. Ei nopeampaa vaihtoehtoa.
                        </div>
                    )}
                </div>
            )}
            {!routeSearchOpen && !navigating && (
                <button
                    type="button"
                    className="search-fab"
                    onClick={handleOpenRouteSearch}
                    aria-label="Avaa reittihaku"
                    title="Reittihaku"
                >
                    <svg viewBox="0 0 24 24" width="24" height="24" aria-hidden="true">
                        <path fill="currentColor" d="M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.61-.22l-2.39.96a7.03 7.03 0 0 0-1.62-.94l-.36-2.54A.5.5 0 0 0 13 2h-2a.5.5 0 0 0-.5.42l-.36 2.54c-.58.24-1.12.56-1.62.94l-2.39-.96a.5.5 0 0 0-.61.22L3.2 8.48a.5.5 0 0 0 .12.64l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94L3.32 14.7a.5.5 0 0 0-.12.64l1.92 3.32c.14.24.43.34.69.22l2.39-.96c.5.38 1.04.7 1.62.94l.36 2.54c.04.24.25.42.5.42h2c.25 0 .46-.18.5-.42l.36-2.54c.58-.24 1.12-.56 1.62-.94l2.39.96c.26.12.55.02.69-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58ZM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7Z"/>
                    </svg>
                </button>
            )}
            {routeSearchOpen ? (
                <section className="planner-toolbar">
                    <div className="user-bar">
                        <span className="user-status">
                            {isAuthenticated ? `👤 ${auth.user.username}` : 'Anonyymi käyttäjä'}
                        </span>
                        <div className="user-actions">
                            <button type="button" className="settings-open" onClick={handleStartLocationTracking}>
                                Seuraa sijaintia
                            </button>
                            <button type="button" className="settings-open" onClick={handleOpenSettings}>
                                Asetukset
                            </button>
                        </div>
                    </div>
                    {!isAuthenticated && (
                        <div className="anonymous-premium-note">
                            Anonyyminä käytät perusreititystä. Kirjautuminen avaa sää- ja sadetiedot, tuulet, tutkat ja viimeisimmän reitin tallennuksen.
                        </div>
                    )}
                    <form className="trip-form" onSubmit={handleSubmit}>
                        <label>
                            Lähtö
                            {startMode === 'location' ? (
                                <div className="start-location-chip">
                                    <span>📍 Oma sijainti</span>
                                    <button type="button" className="link-button" onClick={() => { setStart(''); setStartMode('custom'); }}>
                                        Vaihda
                                    </button>
                                </div>
                            ) : (
                                <div className="start-field">
                                    <AddressInput value={start} onChange={setStart} placeholder="esim. Turku" />
                                    <button
                                        type="button"
                                        className="use-location"
                                        onClick={() => { setStart(''); setStartMode('location'); }}
                                        title="Käytä nykyistä sijaintia"
                                    >
                                        📍
                                    </button>
                                </div>
                            )}
                        </label>
                        <label>
                            Kohde
                            <AddressInput value={end} onChange={setEnd} placeholder="Minne mennään?" />
                        </label>
                        <button
                            type="button"
                            className={`route-options-toggle ${advancedRouteOpen ? 'open' : ''}`}
                            onClick={() => setAdvancedRouteOpen((open) => !open)}
                            aria-expanded={advancedRouteOpen}
                            aria-label="Reittiasetukset"
                            title="Reittiasetukset"
                        >
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M19.4 13.5a7.8 7.8 0 0 0 .1-1.5 7.8 7.8 0 0 0-.1-1.5l2-1.5-2-3.4-2.4 1a7 7 0 0 0-2.6-1.5L14 2.5h-4l-.4 2.6A7 7 0 0 0 7 6.6l-2.4-1-2 3.4 2 1.5a7.8 7.8 0 0 0-.1 1.5 7.8 7.8 0 0 0 .1 1.5l-2 1.5 2 3.4 2.4-1a7 7 0 0 0 2.6 1.5l.4 2.6h4l.4-2.6a7 7 0 0 0 2.6-1.5l2.4 1 2-3.4-2-1.5Z" />
                                <circle cx="12" cy="12" r="3" />
                            </svg>
                        </button>
                        {advancedRouteOpen && (
                            <div className="route-advanced-panel">
                                <div className="travel-mode-field">
                                    <span>Kulkutapa</span>
                                    <div className="travel-mode-buttons" role="radiogroup" aria-label="Kulkutapa">
                                        {travelModes.map((mode) => (
                                            <button
                                                key={mode.value}
                                                type="button"
                                                role="radio"
                                                aria-checked={travelMode === mode.value}
                                                className={`travel-mode-button ${travelMode === mode.value ? 'selected' : ''}`}
                                                onClick={() => handleTravelModeChange(mode.value)}
                                                title={mode.label}
                                            >
                                                <TravelModeIcon mode={mode.value} />
                                                {mode.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="route-preference-field">
                                    <span>Reittivalinta</span>
                                    <div className="route-preference-buttons" role="radiogroup" aria-label="Reittivalinta">
                                        {routePreferences.map((preference) => (
                                            <button
                                                key={preference.value}
                                                type="button"
                                                role="radio"
                                                aria-checked={routePreference === preference.value}
                                                className={`route-preference-button ${routePreference === preference.value ? 'selected' : ''}`}
                                                disabled={preference.value === 'tailwind' && travelMode === 'foot'}
                                                onClick={() => handleRoutePreferenceChange(preference.value)}
                                            >
                                                {preference.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <label>
                                    Lähtöaika
                                    <input
                                        type="datetime-local"
                                        value={departure}
                                        onChange={(event) => setDeparture(event.target.value)}
                                    />
                                </label>
                            </div>
                        )}
                        <button type="submit" className="route-submit-button" disabled={loading}>
                            {loading ? 'Lasketaan' : 'Laske reitti'}
                        </button>
                    </form>
                    {error && <div className="error-message">{error}</div>}
                </section>
            ) : (
                <section className="route-summary-card" aria-label="Valittu reitti">
                    <span>Matka</span>
                    <strong className="route-endpoints">
                        <button type="button" className="edit-endpoint" onClick={() => handleEditEndpoint('start')} title="Vaihda lähtöpaikka">{startMode === 'location' ? 'Oma sijainti' : start}</button>
                        {' → '}
                        <button type="button" className="edit-endpoint" onClick={() => handleEditEndpoint('end')} title="Vaihda kohde">{end}</button>
                    </strong>
                    {selectedRoute && (
                        <small>
                            {metersToKm(selectedRoute.distanceMeters)} · {secondsToHours(selectedRoute.durationSeconds)}
                        </small>
                    )}
                    {routeWeatherSummary && (
                        <small className="weather-summary-line">
                            {Number.isFinite(routeWeatherSummary.avgTemp) ? `🌡️ ${routeWeatherSummary.avgTemp}°C` : ''}
                            {routeWeatherSummary.maxPrecip > 0
                                ? ` · 🌧️ sadetta ${routeWeatherSummary.maxPrecip.toFixed(1)} mm`
                                : ' · ☀️ poutaa'}
                            {Number.isFinite(routeWeatherSummary.wind) && routeWeatherSummary.wind < -0.3
                                ? ` · 🍃 myötätuuli ${Math.abs(routeWeatherSummary.wind)} m/s`
                                : Number.isFinite(routeWeatherSummary.wind) && routeWeatherSummary.wind > 0.3
                                    ? ` · 💨 vastatuuli ${routeWeatherSummary.wind} m/s`
                                    : ''}
                        </small>
                    )}
                    {weatherAlert && (
                        <div className={`weather-change-alert alert-${weatherAlert.level}`} role="status">
                            <strong>{weatherAlert.title}</strong>
                            <span>{weatherAlert.message}</span>
                            <button type="button" onClick={() => setWeatherAlert(null)} aria-label="Kuittaa säävaroitus">×</button>
                        </div>
                    )}
                    {selectedRoute && !routeWeatherSummary && !isAuthenticated && (
                        <small className="weather-summary-line locked-weather">
                            🌦️ Kirjaudu nähdäksesi sään ja tuulet
                        </small>
                    )}
                    {consumptionEstimate && (
                        <small className={`consumption-line ${consumptionEstimate.enough ? '' : 'consumption-warn'}`}>
                            {consumptionEstimate.isEv ? '🔋' : '⛽'} {consumptionEstimate.used.toFixed(1)} {consumptionEstimate.unit}
                            {' · '}toimintamatka {Math.round(consumptionEstimate.rangeKm)} km
                            {!consumptionEstimate.enough && ` · ${consumptionEstimate.isEv ? 'lataus' : 'tankkaus'} tarvitaan`}
                        </small>
                    )}
                    {chargingSuggestion && (
                        <div className="charging-suggestion">
                            <span className="charging-suggestion-icon">⚡</span>
                            <span>
                                Pysähdy lataamaan: <strong>{chargingSuggestion.name}</strong>
                                {chargingSuggestion.operator ? ` (${chargingSuggestion.operator})` : ''}
                                {' '}~{chargingSuggestion.kmFromStart} km kohdalla
                                {' · '}akkua jäljellä ~{chargingSuggestion.remainingPct} %
                                {chargingSuggestion.powerKw ? ` · ${chargingSuggestion.powerKw} kW${chargingSuggestion.fast ? ' pikalataus' : ''}` : ''}
                            </span>
                        </div>
                    )}
                    <div>
                        <button type="button" className="primary" disabled={!selectedRoute || navigationMode === 'gps'} onClick={handlePrepareNavigation}>
                            Aloita
                        </button>
                        <button type="button" disabled={!selectedRoute || navigationMode === 'running'} onClick={handleStartNavigation}>
                            Simuloi
                        </button>
                        <button type="button" onClick={handleOpenRouteSearch}>
                            Muuta
                        </button>
                    </div>
                </section>
            )}

            <section className="planner-content">
                <div className="map-stage">
                    <div ref={mapElement} className="map-panel" />
                    <div ref={popupRef} className="map-popup">
                        <button type="button" className="map-popup-close" onClick={handleClosePopup} aria-label="Sulje">×</button>
                        <span className="map-popup-text">{featureInfo?.text}</span>
                    </div>
                    <div ref={pickRef} className={`map-pick-popup${pickPoint ? ' open' : ''}`}>
                        {pickPoint && (
                            <>
                                <button type="button" className="map-popup-close" onClick={closePick} aria-label="Sulje">×</button>
                                <div className="map-pick-address">
                                    {pickLoading ? 'Haetaan osoitetta…' : (pickAddress?.label || `${pickPoint.lat.toFixed(5)}, ${pickPoint.lon.toFixed(5)}`)}
                                </div>
                                <div className="map-pick-actions">
                                    <button type="button" className="primary" onClick={() => handleSetPickAs('end')}>
                                        Aseta kohteeksi
                                    </button>
                                    <button type="button" onClick={() => handleSetPickAs('start')}>
                                        Lähtöpisteeksi
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                    {navigationMode !== 'idle' && (selectedRoute || navigationMode === 'tracking') && (
                        <div className="navigation-widget" aria-live="polite">
                            <span className="navigation-widget-label">
                                {navigationMode === 'tracking'
                                    ? 'Sijainnin seuranta'
                                    : navigationMode === 'gps' ? 'Navigointi' : 'Simulaatio'}
                            </span>
                            {navigationMode === 'tracking' ? (
                                <>
                                    <strong>Seurataan nykyistä sijaintia</strong>
                                    <small>Ei aktiivista reittisuunnitelmaa</small>
                                </>
                            ) : (
                                <div className="maneuver-card">
                                    <div className="maneuver-icon" aria-hidden="true">
                                        <ManeuverArrow instruction={currentInstruction} />
                                    </div>
                                    <div className="maneuver-copy">
                                        <span className="maneuver-distance">
                                            {currentInstruction ? instructionDistanceLabel(currentInstruction.remainingToNext) : progressToPercent(navigationProgress)}
                                        </span>
                                        <strong>{currentInstruction?.text || 'Jatka reittiä'}</strong>
                                        {currentInstruction?.laneHint && (
                                            <span className="maneuver-lane">{currentInstruction.laneHint}</span>
                                        )}
                                        {currentInstruction && (
                                            <small>{instructionDistanceLabel(currentInstruction.remainingToDestination)} määränpäähän</small>
                                        )}
                                    </div>
                                </div>
                            )}
                            {navigationMode !== 'tracking' && navEta && (
                                <div className="navigation-eta" aria-label="Saapumisennuste">
                                    <div className="navigation-eta-item">
                                        <span className="navigation-eta-value">{navEta.arrivalLabel}</span>
                                        <span className="navigation-eta-label">perillä</span>
                                    </div>
                                    <div className="navigation-eta-item">
                                        <span className="navigation-eta-value">{remainingKmLabel(navEta.remainingMeters)}</span>
                                        <span className="navigation-eta-label">jäljellä</span>
                                    </div>
                                    <div className="navigation-eta-item">
                                        <span className="navigation-eta-value">{remainingTimeLabel(navEta.remainingSeconds)}</span>
                                        <span className="navigation-eta-label">aikaa</span>
                                    </div>
                                </div>
                            )}
                            <div className="navigation-speed-row">
                                <span className={`navigation-speed${isSpeeding ? ' speeding' : ''}`}>
                                    {Number.isFinite(displayedSpeedKmh) ? `${Math.max(0, Math.round(displayedSpeedKmh))} km/h` : '– km/h'}
                                </span>
                            </div>
                            {isSpeeding && <span className="speeding-warning">Ylinopeus</span>}
                            {nextRoadFeature && (
                                <span className="navigation-road-context">
                                    Seuraava: {roadContextLabel(nextRoadFeature)}
                                </span>
                            )}
                            {currentInstruction?.currentRoadText && <span className="navigation-widget-next">Nyt: {currentInstruction.currentRoadText}</span>}
                            {weatherAlert && (
                                <span className={`navigation-weather-alert alert-${weatherAlert.level}`}>
                                    {weatherAlert.title}
                                </span>
                            )}
                            {gpsError && <span className="nav-gps-error">{gpsError}</span>}
                        </div>
                    )}
                    {navigationMode !== 'idle' && selectedRoute && (
                        <div
                            className={`nav-speed-limit${isSpeeding ? ' over' : ''}${Number.isFinite(currentSpeedLimit) ? '' : ' unknown'}`}
                            aria-label={Number.isFinite(currentSpeedLimit) ? `Vallitseva nopeusrajoitus ${currentSpeedLimit}` : 'Nopeusrajoitus ei tiedossa'}
                        >
                            <span className="nav-speed-limit-value">
                                {Number.isFinite(currentSpeedLimit) ? currentSpeedLimit : '–'}
                            </span>
                        </div>
                    )}
                    <button
                        type="button"
                        className="attribution-button"
                        aria-label="Tietolähteet"
                        aria-expanded={attributionOpen}
                        onClick={() => setAttributionOpen((open) => !open)}
                    >
                        ⓘ
                    </button>
                    {attributionOpen && (
                        <div className="attribution-panel" role="dialog" aria-label="Tietolähteet">
                            <div className="attribution-head">
                                <strong>Tietolähteet</strong>
                                <button type="button" className="attribution-close" onClick={() => setAttributionOpen(false)} aria-label="Sulje">×</button>
                            </div>
                            <ul>
                                <li>Kartta &amp; reitit: © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>-tekijät (ODbL)</li>
                                <li>Paikkahaku: <a href="https://nominatim.org/" target="_blank" rel="noreferrer">Nominatim</a> / OpenStreetMap</li>
                                <li>Reititys: <a href="https://www.graphhopper.com/" target="_blank" rel="noreferrer">GraphHopper</a></li>
                                <li>Sää: <a href="https://openweathermap.org/" target="_blank" rel="noreferrer">OpenWeather</a></li>
                                <li>Sadetutka: <a href="https://www.rainviewer.com/" target="_blank" rel="noreferrer">RainViewer</a></li>
                                <li>Liikenne, häiriöt &amp; kelit: <a href="https://www.digitraffic.fi/" target="_blank" rel="noreferrer">Digitraffic</a> / Fintraffic (CC BY 4.0)</li>
                                <li>Latauspisteet: Digitraffic AFIR</li>
                            </ul>
                        </div>
                    )}
                    <div className="map-layer-toggles" aria-label="Karttatasot">
                        <button
                            type="button"
                            className={`layer-toggle state-${radarStatus} ${radarEnabled ? 'active' : ''}`}
                            disabled={!isAuthenticated}
                            aria-pressed={radarEnabled}
                            onClick={() => setRadarEnabled((enabled) => !enabled)}
                            title={!isAuthenticated
                                ? 'Sadetutka (vaatii kirjautumisen)'
                                : `Sadetutka ${radarEnabled ? 'päällä' : 'pois'}${radarStatus === 'error' ? ' – ei lataudu' : radarStatus === 'loading' ? ' – ladataan' : ''}`}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M7 15a4 4 0 1 1 1-7.87A5 5 0 0 1 18 8.5a3.5 3.5 0 0 1 .5 6.5" />
                                <path d="M8 18l-1 2M12 18l-1 2M16 18l-1 2" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            className={`layer-toggle state-${speedCameraStatus} ${speedCamerasEnabled ? 'active' : ''}`}
                            aria-pressed={speedCamerasEnabled}
                            onClick={() => setSpeedCamerasEnabled((enabled) => !enabled)}
                            title={`Nopeuskamerat ${speedCamerasEnabled ? 'päällä' : 'pois'}${speedCameraStatus === 'ready' ? ` – ${speedCameraCount} kpl` : speedCameraStatus === 'error' ? ' – ei lataudu' : speedCameraStatus === 'loading' ? ' – ladataan' : ''}${speedCameraSourceInfo?.dataSource ? ` · ${speedCameraSourceInfo.dataSource}` : ''}`}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M3 8h3l1.5-2h9L18 8h3v11H3z" />
                                <circle cx="12" cy="13" r="3.2" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            className={`layer-toggle state-${roadContextStatus} ${roadContextEnabled ? 'active' : ''}`}
                            aria-pressed={roadContextEnabled}
                            onClick={() => setRoadContextEnabled((enabled) => !enabled)}
                            title={`Tiehuomiot ${roadContextEnabled ? 'päällä' : 'pois'}${roadContextStatus === 'ready' ? ` – ${roadContextFeatures.length} kpl` : roadContextStatus === 'error' ? ' – ei lataudu' : roadContextStatus === 'loading' ? ' – ladataan' : ''}${roadContextSourceInfo?.dataSource ? ` · ${roadContextSourceInfo.dataSource}` : ''}`}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M12 3 2 20h20L12 3Z" />
                                <path d="M12 10v4M12 17h.01" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            className={`layer-toggle state-${digitrafficStatus} ${digitrafficEnabled ? 'active' : ''}`}
                            aria-pressed={digitrafficEnabled}
                            onClick={() => setDigitrafficEnabled((enabled) => !enabled)}
                            title={`Liikennehäiriöt & tietyöt (Digitraffic) ${digitrafficEnabled ? 'päällä' : 'pois'}${digitrafficStatus === 'ready' ? ` – ${digitrafficItems.length} kpl` : digitrafficStatus === 'error' ? ' – ei lataudu' : digitrafficStatus === 'loading' ? ' – ladataan' : ''}`}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M10 4h4l4 16H6L10 4Z" />
                                <path d="M8.5 11h7M7.5 15h9M4 20h16" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            className={`layer-toggle state-${fluencyStatus} ${fluencyEnabled ? 'active' : ''}`}
                            aria-pressed={fluencyEnabled}
                            onClick={() => setFluencyEnabled((enabled) => !enabled)}
                            title={`Liikenteen sujuvuus (Digitraffic TMS) ${fluencyEnabled ? 'päällä' : 'pois'}${fluencyStatus === 'ready' ? ` – ${fluencyItems.length} asemaa` : fluencyStatus === 'error' ? ' – ei lataudu' : fluencyStatus === 'loading' ? ' – ladataan' : ''}`}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M4 18a8 8 0 1 1 16 0" />
                                <path d="M12 18l4-5" />
                            </svg>
                        </button>
                        <button
                            type="button"
                            className={`layer-toggle state-${chargingStatus} ${chargingEnabled ? 'active' : ''}`}
                            aria-pressed={chargingEnabled}
                            onClick={() => setChargingEnabled((enabled) => !enabled)}
                            title={`Latauspisteet (Digitraffic AFIR) ${chargingEnabled ? 'päällä' : 'pois'}${chargingStatus === 'ready' ? ` – ${chargingItems.length} kpl` : chargingStatus === 'error' ? ' – ei lataudu' : chargingStatus === 'loading' ? ' – ladataan' : ''}`}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" />
                            </svg>
                        </button>
                    </div>
                    <div className="map-nav-actions" aria-label="Navigointi">
                        <button
                            type="button"
                            className="nav-action danger"
                            disabled={navigationMode === 'idle'}
                            onClick={handleStopNavigation}
                        >
                            Lopeta navigointi
                        </button>
                        {gpsError && <span className="nav-gps-error">{gpsError}</span>}
                    </div>
                </div>

                <aside className="route-panel">
                    {tripPlan ? (
                        <>
                            <div className="recommendation">
                                <span>Suositus</span>
                                <strong>{tripPlan.recommendation}</strong>
                                {modeWarning && <p className="mode-warning">{modeWarning}</p>}
                                {!tripPlan.weatherEnabled && tripPlan.weatherUnavailableReason && <p>{tripPlan.weatherUnavailableReason}</p>}
                            </div>

                            <div className="navigation-panel">
                                <span>Navigointi</span>
                                <strong>
                                    {navigationMode === 'idle' && 'Valmiina valitulle reitille'}
                                    {navigationMode === 'gps' && 'GPS-seuranta käynnissä'}
                                    {navigationMode === 'running' && 'Simulaatio käynnissä'}
                                    {navigationMode === 'tracking' && 'Sijainnin seuranta käynnissä'}
                                </strong>
                                <div className="navigation-progress">
                                    <span style={{ width: progressToPercent(navigationProgress) }} />
                                </div>
                                <small>{progressToPercent(navigationProgress)} reitistä</small>
                                {navigationMode !== 'idle' && (
                                    <small className="navigation-next-line">{formatSpeed(displayedSpeedKmh)}</small>
                                )}
                                {navigationMode !== 'idle' && currentInstruction && (
                                    <small className="navigation-next-line">
                                        {currentInstruction.text} · {instructionDistanceLabel(currentInstruction.remainingToDestination)} jäljellä
                                    </small>
                                )}
                                {navigationMode !== 'idle' && nextRoadFeature && (
                                    <small className="navigation-next-line">
                                        Seuraava tiehuomio: {roadContextLabel(nextRoadFeature)}
                                    </small>
                                )}
                                <div className="navigation-voice-controls">
                                    <label>
                                        Ääni
                                        <select
                                            value={navigationVoiceKey}
                                            onChange={(event) => setNavigationVoiceKey(event.target.value)}
                                        >
                                            <option value="auto">Automaattinen suomiääni</option>
                                            {navigationVoices.map((voice) => (
                                                <option key={voiceKey(voice)} value={voiceKey(voice)}>
                                                    {voiceOptionLabel(voice)}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <button type="button" onClick={handleTestNavigationVoice}>
                                        Testaa
                                    </button>
                                </div>
                                <small>
                                    {navigationVoices.filter((voice) => (voice.lang || '').toLowerCase().startsWith('fi')).length
                                        ? 'Selain tarjoaa suomenkielisen äänen.'
                                        : 'Selain ei listaa suomenkielistä ääntä. Asenna tai ota käyttöön suomiääni käyttöjärjestelmässä tai valitse pilvi-TTS myöhemmin.'}
                                </small>
                            </div>

                            <div className="route-shortcuts" aria-label="Reitin pikavalinnat">
                                <button
                                    type="button"
                                    className={selectedRouteIndex === (tripPlan.routes[0]?.index ?? 0) ? 'selected' : ''}
                                    onClick={() => setSelectedRouteIndex(tripPlan.routes[0]?.index ?? 0)}
                                >
                                    Nopein
                                </button>
                                <button
                                    type="button"
                                    className={tailwindRouteIndex !== null && selectedRouteIndex === tailwindRouteIndex ? 'selected' : ''}
                                    disabled={tailwindRouteIndex === null || tailwindRouteIndex === (tripPlan.routes[0]?.index ?? 0)}
                                    onClick={() => setSelectedRouteIndex(tailwindRouteIndex)}
                                >
                                    Kevein / myötätuulisin
                                </button>
                            </div>

                            <div className="route-list">
                                {tripPlan.routes.map((route) => (
                                    <button
                                        key={route.index}
                                        type="button"
                                        className={`route-choice ${route.index === selectedRouteIndex ? 'selected' : ''}`}
                                        onClick={() => setSelectedRouteIndex(route.index)}
                                    >
                                        <span className="route-color" style={{ background: routeColors[route.index % routeColors.length] }} />
                                        <span>
                                            <strong>{routeDisplayLabel(route, tailwindRouteIndex)}</strong>
                                            <small>
                                                {metersToKm(route.distanceMeters)} · {secondsToHours(route.durationSeconds)}
                                            </small>
                                            {route.averageHeadwindMetersPerSecond !== null && route.averageHeadwindMetersPerSecond !== undefined && (
                                                <small>
                                                    {route.averageHeadwindMetersPerSecond < 0
                                                        ? `myötätuuli ${Math.abs(route.averageHeadwindMetersPerSecond)} m/s`
                                                        : `vastatuuli ${route.averageHeadwindMetersPerSecond} m/s`}
                                                </small>
                                            )}
                                        </span>
                                        <span className={`risk-pill risk-${route.riskLevel.toLowerCase()}`}>
                                            {riskLabels[route.riskLevel] || route.riskLevel}
                                        </span>
                                    </button>
                                ))}
                            </div>

                            {selectedRoute && tripPlan.weatherEnabled && (
                                <div className="weather-list">
                                    <h2>Sää reitin varrella</h2>
                                    {selectedRoute.weatherPoints.map((point) => {
                                        const impact = windImpact(point.headwindMetersPerSecond);
                                        return (
                                            <div className="weather-row" key={`${point.lat}-${point.lon}-${point.distanceFromStartMeters}`}>
                                                <span className={`weather-dot risk-${point.riskLevel.toLowerCase()}`} />
                                                <div>
                                                    <strong>
                                                        {metersToKm(point.distanceFromStartMeters)} · {formatDateTime(point.arrivalTime)}
                                                    </strong>
                                                    <small>
                                                        {point.temperatureCelsius === null
                                                            ? point.summary
                                                            : `${point.temperatureCelsius} °C, tuuli ${point.windSpeedMetersPerSecond} m/s, sade ${point.precipitationMillimeters} mm`}
                                                    </small>
                                                    {impact && <span className={`wind-impact ${impact.className}`}>{impact.label}</span>}
                                                    {point.summary && <small>{point.summary}</small>}
                                                    {point.description && <small>{point.description}</small>}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {selectedRoute && !tripPlan.weatherEnabled && (
                                <div className="weather-list">
                                    <h2>Sää reitin varrella</h2>
                                    <p className="locked-weather">{tripPlan.weatherUnavailableReason || 'Säädata ei ole käytettävissä.'}</p>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="empty-panel">Laske reitti nähdäksesi vaihtoehdot ja sääpisteet.</div>
                    )}
                </aside>
            </section>
        </main>
    );
};

export default MapComponent;
